import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ensureUiScope } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  canDownloadCustomerPackage,
  canDownloadExternalSafePackage,
  canDownloadInternalPackage,
  canManageAccessScope,
  canManageSafetyCaseArtifacts,
  canAssignSafetyCaseOwner,
  getCapabilitiesFromUser,
  hasCapability,
  hasAnyCapability,
} from '../auth/access';
import {
  reportsApi, type SafetyDashboardRow,
  type SafetyDashboardScanHistoryItem, type SafetyDashboardSummary,
  type SafetyPortfolioResponse, type WorkflowImpactSummary,
} from '../services/reportsApi';
import { sitesApi, type Site } from '../services/sitesApi';
import { useSiteLocationLookups } from '../hooks/useSiteLocationLookups';
import {
  resolvePortfolioCaseDisplayTitle,
  resolvePortfolioCaseTitleDisambiguator,
  resolvePortfolioPhysicalLocation,
  resolvePortfolioRegisterLocationLabel,
  textContainsPeerSiteBuyerAlias,
} from '../domain/safetyCases/safetyPortfolioLocation';
import { buildLogicalScanKey } from '../domain/safetyCases/safetyScanLineage';
import type { SafetyLocationLookups } from '../utils/locationDisplay';

import { resolveMeasurementClaimSummaryLabel } from '../domain/claimReadinessPresentation';
import { buildArtifactFlowPath, buildSafetyReportArtifactPath } from '../utils/reportFlow';
import { resolveSafetyReportOpenPath } from '../utils/safetyReportOpenNavigation';
import { safetyScanVerifyPath } from '../domain/acceptance/scanVerifyNavigation';
import { V1_HAZARD_CLASS_OPTIONS } from './acceptanceProof/hazardClassReportOptions';
import { buildHazardClassReportPath, resolveIncidentHazardClassKey } from '../domain/incidents/incidentHazardClass';
import { SafetyReportSemanticIcon, type SafetyReportIconSemantic } from './safetyReportsIconSystem';
import { resolveSafetyPrimaryBlocker } from '../domain/safetyFindingPrimaryBlocker';
import {
  ACCEPTANCE_UI_LABELS,
  buildLiveSafetyCasePath,
  resolveDraftSafetyScanReportId,
  resolveSafetyCaseId,
  resolveSignedSafetyScanReportId,
} from '../utils/acceptanceHierarchyNavigation';
import { buildAfterSignedScanReviewPath } from '../domain/incidents/afterSignedScanReview';
import {
  buildCreateDraftPayloadFromHandoff,
  buildHandoffFromDashboardRow,
} from '../domain/safetyCases/safetyScanWorkspaceHandoff';
import {
  portfolioCaseStateLabel,
  portfolioOutcomeStateLabel,
  portfolioReviewStateLabel,
} from '../domain/safetyCases/safetyCaseWorklist';
import {
  buildRiskPatternReviewPath,
  formatRiskPatternSummary,
} from '../domain/incidents/riskPatternWorkflowReviewContext';
import {
  buildRiskPatternTitle,
  dedupeCandidateRiskPatterns,
} from '../domain/safetyCases/riskPatternPortfolio';
import {
  attentionFilterParam,
  buildScanOnlyAttentionWorkItems, buildWorkspaceAttention,
  filterWorkspaceAttentionItems,
  resolveAttentionFilter,
  type AttentionMetricKey,
  type WorkspaceAttentionItem,
} from '../domain/safetyCases/workspaceAttention';
import {
  buildPortfolioAttentionItems,
  buildPortfolioAttentionMetrics,
  formatSiteCoverageSummary,
} from '../domain/safetyCases/workspacePortfolioAttention';
import {
  buildPortfolioExportCsv,
  buildPortfolioExportFilename,
  createPortfolioExportBlob,
} from '../domain/safetyCases/workspacePortfolioExport';
import {
  DASHBOARD_FETCH_PAGE,
  buildCaseRegisterRows,
  buildRecordRegisterRows,
  buildRegisterCaseScanLinkSource,
  buildSignedRecordPath,
  buildScanRegisterRows,
  canIncreaseDashboardFetch,
  coerceRegisterRevision,
  mapScanRegisterTrigger,
  mapScanWorkflowState,
  nextDashboardFetchLimit,
  type RecordRegisterRow,
  type ScanRegisterRow,
} from '../domain/safetyCases/workspaceRegisters';
import { collapseCompoundCustomerLabel } from '../domain/safetyCases/workspaceResolvedState';
import { resolveSavedView, savedViewParam } from '../domain/safetyCases/workspaceSavedViews';
import {
  caseRowHasNewActivity,
  resolveWorkspacePrimaryCta,
} from '../domain/safetyCases/workspacePrimaryCta';
import {
  collectTechnicalIds,
  friendlyDisplayLabel,
} from '../domain/safetyCases/workspaceFriendlyLabels';
import { WorkspaceAttentionPanel } from '../components/safetyReports/WorkspaceAttentionPanel';
import { WorkspaceHeaderChrome } from '../components/safetyReports/WorkspaceHeaderChrome';
import { WorkspaceNavGroups } from '../components/safetyReports/WorkspaceNavGroups';
import { WorkspaceRegistersPanel } from '../components/safetyReports/WorkspaceRegistersPanel';
import { WorkspaceSiteComparisonPanel } from '../components/safetyReports/WorkspaceSiteComparisonPanel';
import { WorkspacePortfolioGovernancePanel } from '../components/safetyReports/WorkspacePortfolioGovernancePanel';
import { RiskPatternAttentionDrawer } from '../components/safetyReports/RiskPatternAttentionDrawer';
import { useRiskPatternDrawerDisposition } from '../components/safetyReports/RiskPatternDispositionPanel';
import { NewActivityAttentionDrawer } from '../components/safetyReports/NewActivityAttentionDrawer';
import { useNewActivityDrawerDecision } from '../components/safetyReports/NewActivityDecisionPanel';
import { formatNewActivityCountSummary, resolveHasNewActivityAfterSignedScan } from '../domain/safetyCases/workspaceNewActivityHandling';
import { useWorkspaceCaseRegisterActions } from '../domain/safetyCases/useWorkspaceCaseRegisterActions';
import {
  WORKSPACE_ALL_AUTHORIZED_SITES,
  resolveWorkspacePortfolioScope,
} from '../domain/safetyCases/workspacePortfolioScope';
import {
  WORKSPACE_PAGE_SUBTITLE,
  WORKSPACE_PAGE_TITLE,
  WORKSPACE_TAB_HELPERS,
} from '../domain/safetyCases/safetyCasesWorkspacePresentationCopy';
import { isEngineeringDemoArtifact } from '../domain/buyerFacing/conditionDisplayNames';

function formatWindowLabel(from?: string | null, to?: string | null): string {
  if (!from || !to) return 'All time';
  const f = new Date(from);
  const t = new Date(to);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return 'All time';
  const days = Math.max(1, Math.round((t.getTime() - f.getTime()) / 86400000));
  if (days === 1) return 'Today';
  return `Last ${days} day${days === 1 ? '' : 's'}`;
}

type TimeWindowOptionKey = 'today' | 'last_7_days' | 'last_30_days' | 'custom_range' | 'all_time';

const TIME_WINDOW_OPTIONS: Array<{ key: TimeWindowOptionKey; label: string; disabled?: boolean }> = [
  { key: 'today', label: 'Today' },
  { key: 'last_7_days', label: 'Last 7 days' },
  { key: 'last_30_days', label: 'Last 30 days' },
  { key: 'custom_range', label: 'Custom range', disabled: true },
  { key: 'all_time', label: 'All time' },
];

const WORKSPACE_TABS = ['attention', 'cases', 'scans', 'records'] as const;
type WorkspaceTab = (typeof WORKSPACE_TABS)[number];

function resolveWorkspaceTab(raw: string | null | undefined): WorkspaceTab {
  const value = String(raw || '').trim().toLowerCase();
  return (WORKSPACE_TABS as readonly string[]).includes(value) ? (value as WorkspaceTab) : 'attention';
}

function buildUtcDayRange(days: number): { from: string; to: string } {
  const now = new Date();
  const end = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    23,
    59,
    59,
    999,
  ));
  const start = new Date(end);
  start.setUTCDate(end.getUTCDate() - (days - 1));
  start.setUTCHours(0, 0, 0, 0);
  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
}

function humanizeLabel(value?: string | null): string {
  const text = String(value || '').trim();
  if (!text) return 'Not recorded';
  return text
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatEventTimestamp(value?: string | null): string {
  if (!value) return 'Time not recorded';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Time not recorded';
  const formatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return formatter.format(parsed);
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function appendQuery(path: string, params: Record<string, string>): string {
  const [pathname, search = ''] = path.split('?');
  const next = new URLSearchParams(search);
  Object.entries(params).forEach(([key, value]) => {
    const normalized = String(value || '').trim();
    if (normalized) next.set(key, normalized);
  });
  const query = next.toString();
  return query ? `${pathname}?${query}` : pathname;
}

type PortfolioRow = SafetyDashboardRow & {
  workflow_count?: number;
  blocked_count?: number;
  finalized_count?: number;
  impact_pending_count?: number;
  worsening_count?: number;
  portfolio_summary?: string | null;
  grouped_incident_count?: number;
};

type SafetyScanStatusKind = 'signed' | 'draft' | 'awaiting_signoff';

export type SafetyCaseScanHistoryItem = {
  id: string; reportId: string | null; logicalScanKey: string; scanName: string;
  statusKind: SafetyScanStatusKind; statusLabel: string; scopeStart: string | null; scopeEnd: string | null;
  signedAt: string | null; triggerType: string | null; packageAvailable: boolean; hasNewActivityAfterScan: boolean;
  currentRevision: number | null; signedBy: string | null; signatureState: string | null;
  revisions: Array<{ revision: number; signatureState: string | null; parentRevision: number | null }>;
  actionRow: PortfolioRow; sortTimestamp: number;
  measuredOccurrenceCount?: number | null;
  reviewedOccurrenceCount?: number | null;
  confirmedOccurrenceCount?: number | null;
  usableHours?: number | null;
  coverageState?: string | null;
  verificationState?: string | null;
};

export type GroupedSafetyCase = {
  key: string;
  stableCaseId: string;
  primaryRow: PortfolioRow;
  title: string;
  titleContext: string | null;
  originatingRiskPattern: string;
  hazardCategory: string;
  caseState: string;
  reviewStateLabel: string | null;
  outcomeStateLabel: string | null;
  owner: string | null;
  ownerIdentity: string | null;
  cameraLabel: string | null;
  zoneLabel: string | null;
  deviceLabel: string | null;
  currentScan: SafetyCaseScanHistoryItem | null;
  latestScan: SafetyCaseScanHistoryItem | null;
  latestSignedScan: SafetyCaseScanHistoryItem | null;
  hasNewActivity: boolean;
  latestObservationAt: string | null;
  newActivityCount: number | null;
  reviewState: string | null;
  scanHistory: SafetyCaseScanHistoryItem[];
};

type PortfolioSectionKind = 'candidate' | 'case' | 'category';

function sectionSemanticIcon(kind: PortfolioSectionKind): SafetyReportIconSemantic {
  switch (kind) {
    case 'case':
      return 'blockedBeforeSignoff';
    case 'candidate':
      return 'unsafeConditionAssessment';
    case 'category':
      return 'categorySummary';
    default:
      return 'safetyActionRequired';
  }
}

function normalizeText(value?: string | null): string {
  return String(value || '').trim().toLowerCase();
}

function _resolvePortfolioPrimaryBlocker(row: PortfolioRow) {
  return resolveSafetyPrimaryBlocker({
    isSavedFinding: Boolean(row.finding_saved) && normalizeText(row.report_status) !== 'candidate',
    reviewStatus: row.review_status,
    evidenceStatus: row.package_available ? 'Ready' : 'Not evaluated',
    blockerCodes: Array.isArray(row.blockers) ? row.blockers.map((blocker) => blocker.code) : [],
    recommendedNextStep: row.recommended_next_step,
    followupOwner: resolvePortfolioFollowupOwnerStatus(row),
    correctiveActionStatus: row.corrective_action_status,
    measurementStatus: row.impact_measurement_status,
    packageStatus: row.package_available ? 'available' : 'not_generated',
    signoffStatus: row.signoff_status,
  });
}

function _nextStepLabel(row: PortfolioRow): string {
  switch (normalizeText(row.recommended_next_step)) {
    case 'complete_evidence_review':
      return 'Complete the evidence review and document the control taken or required';
    case 'select_representative_evidence':
      return 'Select representative evidence';
    case 'assign_response_owner':
      return 'Assign response owner';
    case 'complete_corrective_action':
      return 'Record corrective response';
    case 'complete_impact_measurement':
      return 'Continue monitoring';
    case 'continue_monitoring':
      return 'Continue monitoring';
    case 'generate_evidence_package':
      return 'Create a Safety Scan from the active Safety Case when the case is ready for sign-off or sharing';
    case 'sign_off_report':
      return 'Complete Safety Scan sign-off';
    case 'attach_intervention_evidence':
      return 'Attach intervention evidence';
    case 'confirm_controlled_experiment':
      return 'Confirm operating conditions';
    case 'maintain_control':
      return 'Continue routine monitoring';
    case 'resolve_blocked_pattern_reports':
      return 'Resolve Safety Scans blocked before sign-off';
    case 'complete_impact_coverage':
      return 'Close measurement gaps';
    case 'review_worsening_workflows':
      return 'Reassess worsening workflows';
    case 'open_category_report':
      return 'Open category summary';
    default:
      return humanizeLabel(row.recommended_next_step) || 'Open Safety Case';
  }
}

function statusFreshnessLabel(row: PortfolioRow): string {
  if (row.is_status_stale || normalizeText(row.status_source) === 'stale_cached') {
    return 'Status may be out of date. Refresh required.';
  }
  if (row.status_computed_at) {
    return `Status refreshed ${formatEventTimestamp(row.status_computed_at)}`;
  }
  if (normalizeText(row.status_source) === 'cached') {
    return 'Using last saved status snapshot';
  }
  return 'Status refresh not recorded';
}

function riskPatternCardTitle(row: PortfolioRow): string {
  return `Recurring pattern: ${buildRiskPatternTitle(row)}`;
}

function mapHazardCategoryLabel(value?: string | null): string {
  const normalized = normalizeText(value);
  if (!normalized || normalized === 'probe') return 'Needs classification';
  if (normalized === 'restricted_zone' || normalized === 'zone_violation' || normalized.includes('restricted zone')) {
    return 'Restricted Zone Entry';
  }
  if (normalized === 'ppe' || normalized === 'ppe_compliance' || normalized === 'ppe_violation' || normalized.includes('ppe')) {
    return 'PPE Compliance';
  }
  if (normalized === 'forklift_pedestrian' || normalized.includes('forklift') || normalized.includes('vehicle-pedestrian')) {
    return 'Vehicle-Pedestrian Interaction';
  }
  if (normalized === 'blocked_pathway') return 'Blocked Pathway';
  if (normalized === 'working_height') return 'Working at Heights';
  if (normalized.includes('site observation')) return 'Site observation';
  return humanizeLabel(value);
}

function hasNewActivitySinceSignedScan(row: PortfolioRow): boolean {
  // followup_required (active draft) is not itself "new activity" — drafts belong
  // in awaiting sign-off. Acknowledge & continue monitoring must clear this signal.
  return resolveHasNewActivityAfterSignedScan({
    hasSignedScan: Boolean(row.latest_finalized_report_id || resolveSignedSafetyScanReportId(row)),
    hasNewActivitySinceReport: row.has_new_activity_since_report,
    workflowChangedSinceSignoff: row.workflow_changed_since_signoff,
    latestActivityAt: row.latest_observation_at || row.source_workflow_updated_at || null,
    signedAt: row.report_finalized_at || row.latest_scan_signed_at || null,
    acknowledgedThrough: row.acknowledged_through,
  });
}

function categoryLatestScanStateLabel(row: PortfolioRow): string {
  const blocked = Number(row.blocked_count || 0);
  const signed = Number(row.finalized_count || 0);
  if (signed > 0 && blocked > 0) return `${signed} signed · ${blocked} blocked`;
  if (signed > 0) return `${signed} signed`;
  if (blocked > 0) return `${blocked} blocked`;
  return 'No scans yet';
}

function hazardCategoryCardTitle(row: PortfolioRow): string {
  return `Hazard Category: ${resolveHazardCategoryLabel(row)}`;
}

function safeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function trimmedText(value: unknown): string {
  return String(value || '').trim();
}

function formatMeasurementNumber(value: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function formatShortDate(value?: string | null): string | null {
  const text = trimmedText(value);
  if (!text) return null;
  const parsed = new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  }).format(parsed);
}

function formatMeasurementWindow(label: string, start?: string | null, end?: string | null): string | null {
  const startLabel = formatShortDate(start);
  const endLabel = formatShortDate(end);
  if (!startLabel || !endLabel) return null;
  return `${label}: ${startLabel} to ${endLabel}`;
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function measurementClaimSummary(row: PortfolioRow, impactSummary?: WorkflowImpactSummary | null, concise = false): string {
  return resolveMeasurementClaimSummaryLabel({
    claimReadiness: row.claim_readiness,
    impactSummary,
    legacyEffectivenessClaimAllowed: row.effectiveness_claim_allowed,
    concise,
  });
}

type ExposureMeasurementDisplay = {
  headline: string;
  detailLines: string[];
  claimLine?: string;
};

function _buildExposureMeasurementDisplay(row: PortfolioRow): ExposureMeasurementDisplay {
  const impactSummary = row.impact_summary || null;
  const baselineWindow = impactSummary?.baseline_window;
  const followupWindow = impactSummary?.followup_window || impactSummary?.post_action_window;
  const metricName = trimmedText(impactSummary?.metric_name);
  const metricUnit = normalizeText(impactSummary?.metric_unit);
  const denominator = trimmedText(impactSummary?.denominator || impactSummary?.denominator_label);
  const baselineValue = safeNumber(impactSummary?.baseline_value ?? impactSummary?.baseline_exposure_rate);
  const followupValue = safeNumber(impactSummary?.followup_value ?? impactSummary?.post_action_exposure_rate);
  const displayHeadline = trimmedText(impactSummary?.display_headline);
  const displaySummary = trimmedText(impactSummary?.display_summary);
  const explicitPercent = safeNumber(row.exposure_change_percent);
  const derivedPercent = (
    explicitPercent == null
    && baselineValue != null
    && followupValue != null
    && baselineValue !== 0
  ) ? roundToTenth(((baselineValue - followupValue) / baselineValue) * 100) : null;
  const percent = explicitPercent ?? derivedPercent;
  const actionTimestamp = trimmedText(impactSummary?.corrective_action_timestamp);
  const interventionReference = trimmedText(impactSummary?.intervention_reference);
  const requiresDenominator = metricUnit === 'rate' || Boolean(denominator);
  const hasCompleteContext = Boolean(
    metricName
    && metricUnit
    && baselineWindow?.start
    && baselineWindow?.end
    && followupWindow?.start
    && followupWindow?.end
    && baselineValue != null
    && followupValue != null
    && percent != null
    && (!requiresDenominator || denominator)
    && (actionTimestamp || interventionReference)
  );

  if (!hasCompleteContext || percent == null || baselineValue == null || followupValue == null) {
    return {
      headline: displayHeadline || 'Not ready for interpretation',
      detailLines: [
        ...(displaySummary ? [displaySummary] : []),
        'Missing: Baseline window, follow-up window, and measurement unit.',
        'Effectiveness claim: Not available yet.',
        'Helper: A claim requires baseline/follow-up windows, measurement unit, and corrective-action evidence.',
      ],
    };
  }

  const percentValue = percent;
  const baselineMeasurementValue = baselineValue;
  const followupMeasurementValue = followupValue;
  let direction = 'changed';
  if (percentValue > 0) direction = 'lower';
  if (percentValue < 0) direction = 'higher';
  const metricDescriptor = metricUnit === 'rate'
    ? `${metricName} / ${denominator}`
    : metricUnit === 'count'
      ? `${metricName} count`
      : denominator
        ? `${metricName} (${metricUnit} / ${denominator})`
        : `${metricName} (${metricUnit})`;
  const detailLines = [
    `${formatMeasurementNumber(baselineMeasurementValue)} -> ${formatMeasurementNumber(followupMeasurementValue)} ${metricDescriptor}`,
    formatMeasurementWindow('Baseline', baselineWindow?.start, baselineWindow?.end),
    formatMeasurementWindow('Follow-up', followupWindow?.start, followupWindow?.end),
    actionTimestamp ? `Action timestamp: ${formatEventTimestamp(actionTimestamp)}` : `Intervention reference: ${interventionReference}`,
  ].filter(Boolean) as string[];

  return {
    headline: displayHeadline || `${Math.abs(percentValue).toFixed(1)}% ${direction}`,
    detailLines: displaySummary ? [displaySummary, ...detailLines] : detailLines,
    claimLine: `Claim: ${measurementClaimSummary(row, impactSummary, true)}`,
  };
}

function dateTimestamp(value?: string | null): number {
  const parsed = new Date(String(value || ''));
  const ms = parsed.getTime();
  return Number.isFinite(ms) ? ms : 0;
}

export function isAdmittedSafetyCase(row: PortfolioRow): boolean {
  const admission = String(row.case_admission_status || '').trim().toLowerCase();
  if (admission === 'technical_anchor') return false;
  return row.is_managed_case !== false
    && row.safety_case_admitted !== false
    && Boolean(row.is_managed_case ?? row.safety_case_admitted ?? resolveSafetyCaseId(row) ?? row.finding_saved);
}

function hasExistingSafetyCase(row: PortfolioRow): boolean {
  if (resolveSafetyCaseId(row)) return true;
  if (Boolean(row.finding_saved)) return true;
  return ['draft', 'blocked', 'finalized'].includes(normalizeText(row.report_status));
}

function isCandidateRiskPattern(row: PortfolioRow): boolean {
  if (resolveSafetyCaseId(row) || row.finding_saved) return false;
  const reportStatus = normalizeText(row.report_status);
  if (reportStatus === 'candidate') return true;
  return !reportStatus && normalizeText(row.review_status) !== 'reviewed';
}

function resolveHazardCategoryLabel(row: PortfolioRow): string {
  const explicit = String(row.hazard_category || '').trim();
  if (explicit) return mapHazardCategoryLabel(explicit);
  if (String(row.risk_label || '').trim()) return mapHazardCategoryLabel(row.risk_label);
  if (String(row.risk_type || '').trim()) return mapHazardCategoryLabel(row.risk_type);
  return 'Needs classification';
}

function resolveCaseOwner(row: PortfolioRow): string | null {
  if (row.has_followup_owner === false) return null;
  const owner = String(row.followup_owner || '').trim();
  const team = String(row.followup_team || '').trim();
  if (team && owner && team.toLowerCase() !== owner.toLowerCase()) return `${team} / ${owner}`;
  return owner || team || String(row.owner_name || row.owner || '').trim() || null;
}

function resolveCaseOwnerDisplayLabel(row: PortfolioRow): string {
  return resolveCaseOwner(row) || 'Not assigned';
}

function resolveCaseOwnerIdentity(row: PortfolioRow): string | null {
  if (row.has_followup_owner === false) return null;
  return String(row.followup_owner || '').trim() || null;
}

function resolveAttentionDueAt(row: PortfolioRow): string | null {
  const source = row as PortfolioRow & Record<string, any>;
  const activeActionDueDates = (source.corrective_actions?.active_actions || [])
    .filter((action: Record<string, any>) => {
      const status = normalizeText(action.status);
      return !action.completed_at && !action.validated_at && !['completed', 'validated', 'waived', 'cancelled'].includes(status);
    })
    .map((action: Record<string, any>) => action.due_date);
  const candidates = [
    source.corrective_action?.due_date,
    ...activeActionDueDates,
    source.action_snapshot?.next_review_at,
    source.escalation?.next_review_at,
  ].filter((value): value is string => Boolean(String(value || '').trim()));
  const valid = candidates
    .map((value) => ({ value, ms: new Date(value).getTime() }))
    .filter((entry) => Number.isFinite(entry.ms))
    .sort((left, right) => left.ms - right.ms);
  return valid[0]?.value || null;
}

function resolvePortfolioFollowupOwnerStatus(row: PortfolioRow): string {
  if (row.has_followup_owner === false) return 'Not assigned';
  return resolveCaseOwnerDisplayLabel(row);
}

function resolveSafetyCaseStableKey(row: PortfolioRow): string {
  return String(row.safety_case_id || row.finding_id || row.workflow_id || '').trim();
}

function resolveZoneLabel(
  row: PortfolioRow,
  lookups: SafetyLocationLookups = {},
): string | null {
  // Shared customer Location rule — never Demo* / raw id chrome / Warehouse #2.
  const sanitized = resolvePortfolioRegisterLocationLabel(row, lookups);
  if (sanitized && sanitized !== '—') return sanitized;
  return null;
}

function resolveCameraLabel(row: PortfolioRow): string | null {
  const label = friendlyDisplayLabel(row.camera_label, row.camera_id);
  // Peer-site camera nicknames belong under Technical details, not Cases titles.
  if (label && textContainsPeerSiteBuyerAlias(label)) return null;
  return label;
}

function resolveDeviceLabel(row: PortfolioRow): string | null {
  return friendlyDisplayLabel(row.device_label, row.device_id);
}

function resolveTechnicalIdEntries(row: PortfolioRow) {
  return collectTechnicalIds({
    cameraId: row.camera_id,
    cameraLabel: row.camera_label,
    deviceId: row.device_id,
    deviceLabel: row.device_label,
    zoneId: row.zone_id,
    zoneLabel: row.zone_label || row.area_label,
    workflowId: row.workflow_id,
  });
}

function PortfolioTechnicalDetails({
  row,
  testId,
}: {
  row: PortfolioRow;
  testId: string;
}) {
  const entries = resolveTechnicalIdEntries(row);
  if (entries.length === 0) return null;
  return (
    <Accordion
      disableGutters
      elevation={0}
      data-testid={testId}
      sx={{
        borderRadius: '8px !important',
        border: '1px solid rgba(15, 23, 42, 0.08)',
        '&:before': { display: 'none' },
        bgcolor: 'transparent',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Technical details</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
        <Stack spacing={0.5}>
          {entries.map((entry) => (
            <Typography key={`${entry.label}:${entry.value}`} variant="body2" color="text.secondary">
              {`${entry.label}: ${entry.value}`}
            </Typography>
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}


function buildSafetyCaseBaseTitle(
  row: PortfolioRow,
  lookups: SafetyLocationLookups = {},
): string {
  return resolvePortfolioCaseDisplayTitle(
    row,
    lookups,
    formatRiskPatternSummary(row),
  );
}

function buildSafetyCaseDisambiguator(
  row: PortfolioRow,
  lookups: SafetyLocationLookups = {},
): string | null {
  const fromRegistry = resolvePortfolioCaseTitleDisambiguator(row, lookups, {
    cameraName: resolveCameraLabel(row),
  });
  if (fromRegistry) {
    return fromRegistry.startsWith('Camera ') ? fromRegistry : `Zone ${fromRegistry}`;
  }
  const deviceLabel = resolveDeviceLabel(row);
  if (deviceLabel && !textContainsPeerSiteBuyerAlias(deviceLabel)) {
    return `Device ${deviceLabel}`;
  }
  return null;
}

function resolveLatestObservationAt(row: PortfolioRow): string | null {
  return String(row.latest_observation_at || row.source_workflow_updated_at || row.updated_at || '').trim() || null;
}

function resolveNewActivityCount(row: PortfolioRow): number | null {
  const raw = row.new_activity_count ?? row.grouped_incident_count;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function resolveNewActivityReviewState(row: PortfolioRow): string | null {
  const explicit = String(row.review_state || row.latest_review_status_label || row.latest_review_status || '').trim();
  if (explicit) return explicit;
  return hasNewActivitySinceSignedScan(row) ? 'Pending review' : null;
}

function resolveScanStatus(row: PortfolioRow, statusOverride?: string | null): { kind: SafetyScanStatusKind; label: string } {
  if (['signed', 'finalized'].includes(normalizeText(statusOverride))) return { kind: 'signed', label: 'Signed' };
  // Slice D: prefer server-resolved label; never keep customer "Draft" as Scan workflow badge.
  const workflowState = row.customer_scan_workflow_state || mapScanWorkflowState({
    reportStatus: statusOverride ?? row.report_status,
    signoffStatus: row.signoff_status,
    impactMeasurementStatus: row.impact_measurement_status,
    recommendedNextStep: row.recommended_next_step,
    blockerCodes: (row.blockers || []).map((blocker) => String(blocker.code || '')),
    customerScanWorkflowState: row.customer_scan_workflow_state,
  });
  if (workflowState === 'Signed') {
    return { kind: 'signed', label: 'Signed' };
  }
  if (workflowState === 'Awaiting sign-off') {
    return { kind: 'awaiting_signoff', label: 'Awaiting sign-off' };
  }
  if (workflowState === 'Insufficient coverage' || workflowState === 'Failed' || workflowState === 'Review required') {
    return { kind: 'awaiting_signoff', label: workflowState };
  }
  return { kind: 'awaiting_signoff', label: workflowState || 'Awaiting sign-off' };
}

function buildSafetyScanName(start?: string | null, end?: string | null): string {
  const startLabel = formatShortDate(start);
  const endLabel = formatShortDate(end);
  if (startLabel && endLabel) return `Scan - ${startLabel} to ${endLabel}`;
  if (endLabel) return `Scan - Through ${endLabel}`;
  return 'Safety Scan';
}

function buildHistoryActionRow(
  baseRow: PortfolioRow,
  item: Partial<SafetyCaseScanHistoryItem> & {
    reportId?: string | null;
    statusKind: SafetyScanStatusKind;
    scopeStart?: string | null;
    scopeEnd?: string | null;
    signedAt?: string | null;
    packageAvailable?: boolean;
  },
): PortfolioRow {
  if (item.statusKind === 'signed') {
    return {
      ...baseRow,
      report_status: 'finalized',
      latest_finalized_report_id: item.reportId || baseRow.latest_finalized_report_id || baseRow.report_id,
      report_id: item.reportId || baseRow.report_id,
      report_finalized_at: item.signedAt || baseRow.report_finalized_at,
      report_scope_start: item.scopeStart || baseRow.report_scope_start,
      report_scope_end: item.scopeEnd || baseRow.report_scope_end,
      package_available: item.packageAvailable ?? baseRow.package_available,
    };
  }
  return {
    ...baseRow,
    report_status: 'draft',
    active_draft_report_id: item.reportId || baseRow.active_draft_report_id || baseRow.report_id,
    report_id: item.reportId || baseRow.report_id,
    report_created_at: item.signedAt || baseRow.report_created_at || baseRow.updated_at,
    report_scope_start: item.scopeStart || baseRow.report_scope_start,
    report_scope_end: item.scopeEnd || baseRow.report_scope_end,
    customer_scan_workflow_state: item.statusLabel || baseRow.customer_scan_workflow_state,
    package_available: item.packageAvailable ?? baseRow.package_available,
  };
}

function buildSyntheticScanHistoryItems(row: PortfolioRow): SafetyCaseScanHistoryItem[] {
  const items: SafetyCaseScanHistoryItem[] = [];
  const seen = new Set<string>();
  const pushItem = (item: Omit<SafetyCaseScanHistoryItem, 'actionRow'>) => {
    if (seen.has(item.id)) return;
    seen.add(item.id);
    items.push({
      ...item,
      actionRow: buildHistoryActionRow(row, item),
    });
  };
  const explicitHistory: Array<{ item: SafetyDashboardScanHistoryItem; forcedStatus?: string | null }> = [
    ...(row.current_scan ? [{ item: row.current_scan }] : []),
    ...(row.latest_signed_scan ? [{ item: row.latest_signed_scan, forcedStatus: 'signed' }] : []),
    ...(Array.isArray(row.scan_history) ? (row.scan_history as SafetyDashboardScanHistoryItem[]).map((item) => ({ item })) : []),
  ];
  explicitHistory.forEach(({ item: historyItem, forcedStatus }) => {
    const reportId = String(historyItem.report_id || historyItem.scan_id || '').trim() || null;
    const status = resolveScanStatus(row, forcedStatus ?? historyItem.status);
    const logicalScanKey = buildLogicalScanKey({ reportId, scanId: historyItem.scan_id, durableReportId: historyItem.durable_report_id, reportFamilyId: historyItem.report_family_id, derivedFromReportId: historyItem.derived_from_report_id, predecessorReportId: historyItem.predecessor_report_id, scopeStart: historyItem.scope_start, scopeEnd: historyItem.scope_end });
    const revisions = Array.isArray(historyItem.revisions)
      ? historyItem.revisions
        .filter((entry) => typeof entry?.revision === 'number' && Number(entry.revision) >= 1)
        .map((entry) => ({
          revision: Number(entry.revision),
          signatureState: entry.signature_state ? String(entry.signature_state) : null,
          parentRevision: typeof entry.parent_revision === 'number' ? entry.parent_revision : null,
        }))
      : [];
    pushItem({
      id: `${status.kind}:${reportId || historyItem.scan_name || historyItem.signed_at || items.length}`,
      reportId,
      logicalScanKey,
      scanName: String(historyItem.scan_name || '').trim() || buildSafetyScanName(historyItem.scope_start, historyItem.scope_end),
      statusKind: status.kind,
      statusLabel: status.label,
      scopeStart: historyItem.scope_start || null,
      scopeEnd: historyItem.scope_end || null,
      signedAt: historyItem.signed_at || null,
      triggerType: historyItem.trigger_type
        || (status.kind === 'signed' ? row.latest_scan_trigger_type : row.active_draft_trigger_type)
        || null,
      packageAvailable: Boolean(historyItem.package_available ?? historyItem.download_available),
      hasNewActivityAfterScan: Boolean(historyItem.has_new_activity_after_this_scan),
      currentRevision: coerceRegisterRevision({
        revision: historyItem.current_revision,
        revisions: historyItem.revisions,
      }),
      signedBy: String(historyItem.signed_by || '').trim() || null,
      signatureState: historyItem.signature_state ? String(historyItem.signature_state) : null,
      revisions,
      sortTimestamp: dateTimestamp(historyItem.signed_at || historyItem.scope_end || historyItem.scope_start || row.updated_at),
      measuredOccurrenceCount: historyItem.measured_observation_count ?? null,
      reviewedOccurrenceCount: historyItem.reviewed_observation_count ?? null,
      confirmedOccurrenceCount: historyItem.confirmed_observation_count ?? null,
      usableHours: historyItem.usable_hours ?? null,
      coverageState: historyItem.coverage_state ?? null,
      verificationState: historyItem.verification_state ?? null,
    });
  });
  const finalizedId = String(row.latest_finalized_report_id || '').trim();
  if (finalizedId) {
    pushItem({
      id: `signed:${finalizedId}`,
      reportId: finalizedId,
      logicalScanKey: buildLogicalScanKey({ reportId: finalizedId, scopeStart: row.report_scope_start, scopeEnd: row.report_scope_end }),
      scanName: String(row.latest_scan_name || row.safety_scan_title || '').trim()
        || buildSafetyScanName(row.report_scope_start, row.report_scope_end),
      statusKind: 'signed',
      statusLabel: 'Signed',
      scopeStart: row.report_scope_start || null,
      scopeEnd: row.report_scope_end || null,
      signedAt: row.report_finalized_at || row.latest_scan_signed_at || row.report_created_at || null,
      triggerType: row.latest_scan_trigger_type || null,
      packageAvailable: Boolean(row.package_available),
      hasNewActivityAfterScan: hasNewActivitySinceSignedScan(row),
      currentRevision: coerceRegisterRevision({
        revision: row.latest_scan_current_revision,
      }),
      signedBy: String(row.latest_scan_signed_by || '').trim() || null,
      signatureState: 'signed',
      revisions: [],
      sortTimestamp: dateTimestamp(row.report_finalized_at || row.latest_scan_signed_at || row.report_created_at || row.updated_at),
    });
  }
  const draftId = String(row.active_draft_report_id || '').trim();
  if (draftId) {
    const draftStatus = resolveScanStatus({ ...row, report_status: row.report_status || 'draft' });
    pushItem({
      id: `draft:${draftId}`,
      reportId: draftId,
      logicalScanKey: buildLogicalScanKey({ reportId: draftId, scopeStart: row.report_scope_start, scopeEnd: row.report_scope_end }),
      scanName: String(row.latest_scan_name || row.safety_scan_title || '').trim()
        || buildSafetyScanName(row.report_scope_start, row.report_scope_end),
      statusKind: draftStatus.kind,
      statusLabel: draftStatus.label,
      scopeStart: row.report_scope_start || null,
      scopeEnd: row.report_scope_end || null,
      signedAt: row.report_created_at || row.updated_at || null,
      triggerType: row.active_draft_trigger_type || null,
      packageAvailable: Boolean(row.package_available),
      hasNewActivityAfterScan: false,
      currentRevision: null,
      signedBy: null,
      signatureState: 'unsigned',
      revisions: [],
      sortTimestamp: dateTimestamp(row.report_created_at || row.updated_at),
    });
  }
  const reportId = String(row.report_id || '').trim();
  if (
    reportId
    && !draftId
    && normalizeText(row.report_status) !== 'candidate'
    && (normalizeText(row.report_status) !== 'finalized' || !finalizedId)
  ) {
    const status = resolveScanStatus(row);
    pushItem({
      id: `${status.kind}:${reportId}`,
      reportId,
      logicalScanKey: buildLogicalScanKey({ reportId, scopeStart: row.report_scope_start, scopeEnd: row.report_scope_end }),
      scanName: String(row.latest_scan_name || row.safety_scan_title || '').trim()
        || buildSafetyScanName(row.report_scope_start, row.report_scope_end),
      statusKind: status.kind,
      statusLabel: status.label,
      scopeStart: row.report_scope_start || null,
      scopeEnd: row.report_scope_end || null,
      signedAt: row.report_finalized_at || row.report_created_at || row.updated_at || null,
      triggerType: status.kind === 'signed'
        ? (row.latest_scan_trigger_type || null)
        : (row.active_draft_trigger_type || null),
      packageAvailable: Boolean(row.package_available),
      hasNewActivityAfterScan: status.kind === 'signed' ? hasNewActivitySinceSignedScan(row) : false,
      currentRevision: null,
      signedBy: null,
      signatureState: status.kind === 'signed' ? 'signed' : 'unsigned',
      revisions: [],
      sortTimestamp: dateTimestamp(row.report_finalized_at || row.report_created_at || row.updated_at),
    });
  }
  return items;
}

export function groupSafetyCases(rows: PortfolioRow[], lookups: SafetyLocationLookups = {}): GroupedSafetyCase[] {
  const grouped = new Map<string, PortfolioRow[]>();
  rows.forEach((row) => {
    if (!hasExistingSafetyCase(row)) return;
    const groupKey = resolveSafetyCaseStableKey(row);
    if (!groupKey) return;
    const bucket = grouped.get(groupKey) || [];
    bucket.push(row);
    grouped.set(groupKey, bucket);
  });

  const groupedCases = Array.from(grouped.entries()).map(([key, groupedRows]) => {
    const sortedRows = [...groupedRows].sort((left, right) => {
      const leftScore = Math.max(
        dateTimestamp(left.latest_observation_at || left.source_workflow_updated_at || left.updated_at),
        dateTimestamp(left.report_finalized_at || left.report_created_at),
      );
      const rightScore = Math.max(
        dateTimestamp(right.latest_observation_at || right.source_workflow_updated_at || right.updated_at),
        dateTimestamp(right.report_finalized_at || right.report_created_at),
      );
      return rightScore - leftScore;
    });
    const primaryRow = sortedRows[0];
    const historyMap = new Map<string, SafetyCaseScanHistoryItem>();
    sortedRows.forEach((row) => {
      buildSyntheticScanHistoryItems(row).forEach((item) => {
        const existing = historyMap.get(item.logicalScanKey);
        const itemActive = item.statusKind !== 'signed';
        const existingActive = existing ? existing.statusKind !== 'signed' : false;
        if (
          !existing
          || (itemActive && !existingActive)
          || (itemActive === existingActive && item.sortTimestamp > existing.sortTimestamp)
        ) {
          historyMap.set(item.logicalScanKey, item);
        }
      });
    });
    const scanHistory = Array.from(historyMap.values()).sort((left, right) => right.sortTimestamp - left.sortTimestamp);
    const backendCurrentId = String(
      primaryRow.current_scan?.report_id
      || primaryRow.current_scan?.scan_id
      || primaryRow.active_draft_report_id
      || '',
    ).trim();
    const currentScan = backendCurrentId
      ? (scanHistory.find((item) => (
        item.statusKind !== 'signed'
        && String(item.reportId || '').trim() === backendCurrentId
      )) || scanHistory.find((item) => item.statusKind !== 'signed') || null)
      : null;
    const latestScan = currentScan || scanHistory[0] || null;
    const latestSignedScan = scanHistory.find((item) => item.statusKind === 'signed') || null;
    const latestObservationAt = sortedRows
      .map((row) => resolveLatestObservationAt(row))
      .filter(Boolean)
      .sort((left, right) => dateTimestamp(right) - dateTimestamp(left))[0] || null;
    // Prefer workflow/observation watermarks for membership — never finding.updated_at,
    // which advances on acknowledge and would re-open Attention incorrectly.
    const latestActivityAt = sortedRows
      .map((row) => String(row.latest_observation_at || row.source_workflow_updated_at || '').trim() || null)
      .filter(Boolean)
      .sort((left, right) => dateTimestamp(right!) - dateTimestamp(left!))[0] || null;
    const acknowledgedThrough = sortedRows
      .map((row) => String(row.acknowledged_through || '').trim())
      .filter(Boolean)
      .sort((left, right) => dateTimestamp(right) - dateTimestamp(left))[0] || null;
    const newActivityCount = sortedRows
      .map((row) => resolveNewActivityCount(row))
      .find((value) => value != null) ?? null;
    const reviewState = sortedRows
      .map((row) => resolveNewActivityReviewState(row))
      .find(Boolean) || null;
    const hasNewActivity = resolveHasNewActivityAfterSignedScan({
      hasSignedScan: Boolean(latestSignedScan),
      hasNewActivitySinceReport: sortedRows.some((row) => Boolean(row.has_new_activity_since_report)),
      workflowChangedSinceSignoff: sortedRows.some((row) => Boolean(row.workflow_changed_since_signoff)),
      latestActivityAt,
      signedAt: latestSignedScan?.signedAt || null,
      acknowledgedThrough,
    });
    const originatingRiskPattern = formatRiskPatternSummary(primaryRow);
    return {
      key,
      stableCaseId: key,
      primaryRow,
      title: buildSafetyCaseBaseTitle(primaryRow, lookups),
      titleContext: null,
      originatingRiskPattern,
      hazardCategory: resolveHazardCategoryLabel(primaryRow),
      caseState: portfolioCaseStateLabel(primaryRow),
      reviewStateLabel: portfolioReviewStateLabel(primaryRow),
      outcomeStateLabel: portfolioOutcomeStateLabel(primaryRow),
      owner: resolveCaseOwner(primaryRow),
      ownerIdentity: resolveCaseOwnerIdentity(primaryRow),
      cameraLabel: resolveCameraLabel(primaryRow),
      zoneLabel: resolveZoneLabel(primaryRow, lookups),
      deviceLabel: resolveDeviceLabel(primaryRow),
      currentScan,
      latestScan,
      latestSignedScan,
      hasNewActivity,
      latestObservationAt,
      newActivityCount,
      reviewState,
      scanHistory,
    };
  });

  const titleCounts = new Map<string, number>();
  groupedCases.forEach((item) => {
    titleCounts.set(item.title, (titleCounts.get(item.title) || 0) + 1);
  });

  const disambiguatedCases = groupedCases.map((item) => {
    if ((titleCounts.get(item.title) || 0) <= 1) return item;
    const disambiguator = buildSafetyCaseDisambiguator(item.primaryRow, lookups);
    if (!disambiguator) return item;
    return {
      ...item,
      title: `${item.title} - ${disambiguator}`,
      titleContext: disambiguator,
    };
  });

  return disambiguatedCases.sort((left, right) => {
    const leftPriority = left.hasNewActivity ? 0 : 1;
    const rightPriority = right.hasNewActivity ? 0 : 1;
    if (leftPriority !== rightPriority) return leftPriority - rightPriority;
    return dateTimestamp(right.latestObservationAt || right.latestScan?.signedAt) - dateTimestamp(left.latestObservationAt || left.latestScan?.signedAt);
  });
}

function buildReviewPath(row: SafetyDashboardRow, fallbackSiteId: string): string {
  return buildRiskPatternReviewPath(row, fallbackSiteId);
}

function FinalizedScanSecondaryExports({
  row,
  onDownloadCsv,
  onDownloadDocx,
  enabled = true,
}: {
  row: PortfolioRow;
  onDownloadCsv: (row: PortfolioRow) => Promise<void>;
  onDownloadDocx: (row: PortfolioRow) => Promise<void>;
  enabled?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!enabled || normalizeText(row.report_status) !== 'finalized') return null;

  return (
    <Stack spacing={0.75}>
      {!expanded ? (
        <Button
          size="small"
          variant="text"
          onClick={() => setExpanded(true)}
          sx={{ alignSelf: 'flex-start', px: 0, minWidth: 0, textTransform: 'none' }}
        >
          More exports
        </Button>
      ) : (
        <>
          <Button size="small" variant="outlined" onClick={() => void onDownloadCsv(row)}>
            Download CSV
          </Button>
          <Button size="small" variant="outlined" onClick={() => void onDownloadDocx(row)}>
            Download DOCX
          </Button>
        </>
      )}
    </Stack>
  );
}

type PortfolioSectionProps = {
  kind: PortfolioSectionKind;
  title: string;
  description: string;
  rows: PortfolioRow[] | GroupedSafetyCase[];
  emptyState: string;
  loading: boolean;
  actionWorkflowId: string | null;
  onOpenDetail: (row: PortfolioRow, extraParams?: Record<string, string>) => Promise<void>;
  onOpenReview: (row: PortfolioRow) => void;
  onOpenSafetyIntelligence: (row: PortfolioRow) => void;
  onSaveFinding: (row: PortfolioRow) => Promise<void>;
  onCreateOrResumeDraft: (row: PortfolioRow) => Promise<void>;
  onDownloadPackage: (row: PortfolioRow, options?: { rev?: number | null }) => Promise<void>;
  onDownloadCsv: (row: PortfolioRow) => Promise<void>;
  onDownloadDocx: (row: PortfolioRow) => Promise<void>;
  onOpenLiveCase: (row: PortfolioRow) => void;
  onViewSignedScan: (row: PortfolioRow) => Promise<void>;
  onOpenDraftScan: (row: PortfolioRow) => Promise<void>;
  canManageSafetyCases: boolean;
  canDownloadInternalPackage: boolean;
  canDownloadCustomerPackage: boolean;
  canDownloadExternalSafePackage: boolean;
};

const PortfolioSection: React.FC<PortfolioSectionProps> = ({
  kind,
  title,
  description,
  rows,
  emptyState,
  loading,
  actionWorkflowId,
  onOpenDetail,
  onOpenReview,
  onOpenSafetyIntelligence: _onOpenSafetyIntelligence,
  onSaveFinding,
  onCreateOrResumeDraft,
  onDownloadPackage,
  onDownloadCsv,
  onDownloadDocx,
  onOpenLiveCase,
  onViewSignedScan,
  onOpenDraftScan,
  canManageSafetyCases,
  canDownloadInternalPackage,
  canDownloadCustomerPackage,
  canDownloadExternalSafePackage,
}) => {
  const resolvePrimaryAction = useCallback((row: PortfolioRow) => {
    if (kind === 'candidate') {
      return { label: ACCEPTANCE_UI_LABELS.reviewRiskPattern, onClick: () => onOpenReview(row) };
    }
    if (kind === 'case') {
      return { label: ACCEPTANCE_UI_LABELS.openLiveSafetyCase, onClick: () => onOpenLiveCase(row) };
    }
    if (kind === 'category') {
      return { label: 'View Category Summary', onClick: () => void onOpenDetail(row) };
    }
    return { label: 'View Summary', onClick: () => void onOpenDetail(row) };
  }, [kind, onOpenDetail, onOpenLiveCase, onOpenReview]);
  const headerSemantic = sectionSemanticIcon(kind);

  if (loading) {
    return (
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <SafetyReportSemanticIcon semantic={headerSemantic} size="section" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
          </Stack>
          <Skeleton variant="rectangular" height={180} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        borderColor: kind === 'candidate' ? 'rgba(37, 99, 235, 0.18)' : undefined,
        bgcolor: kind === 'candidate' ? 'rgba(37, 99, 235, 0.02)' : undefined,
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <SafetyReportSemanticIcon semantic={headerSemantic} size="section" />
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{title}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        {rows.length === 0 ? (
          kind === 'case' ? (
            <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <Box
                sx={{ display: 'grid', placeItems: 'center' }}
              >
                <SafetyReportSemanticIcon semantic="blockedBeforeSignoff" size="hero" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{emptyState}</Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                Safety Cases appear here after a recurring pattern has been reviewed and linked.
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">{emptyState}</Typography>
          )
        ) : (
          <Stack spacing={2}>
            {rows.map((entry) => {
              if (kind === 'candidate') {
                const row = entry as PortfolioRow;
                const workflowKey = String(row.workflow_id || row.report_id || row.representative_incident_id || row.risk_type || title);
                const busy = actionWorkflowId === String(row.workflow_id || '').trim();
                const primaryAction = resolvePrimaryAction(row);
                return (
                  <Card key={workflowKey} variant="outlined" sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                      <Stack spacing={2}>
                      <Box
                        sx={{
                          display: 'grid',
                          gap: 2,
                          gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.2fr) repeat(3, minmax(0, 0.8fr)) auto' },
                          alignItems: 'start',
                        }}
                      >
                        <Stack spacing={0.75}>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {riskPatternCardTitle(row)}
                          </Typography>
                          <Typography variant="body2">
                            {`Hazard Category: ${resolveHazardCategoryLabel(row)}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Site: ${String(row.site_label || row.site_id || 'Not recorded')}`}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {`Area / Zone: ${resolveZoneLabel(row) || 'Not recorded'}`}
                          </Typography>
                          {resolveCameraLabel(row) ? (
                            <Typography variant="body2" color="text.secondary">
                              {`Camera: ${resolveCameraLabel(row)}`}
                            </Typography>
                          ) : null}
                          {resolveDeviceLabel(row) ? (
                            <Typography variant="body2" color="text.secondary">
                              {`Device: ${resolveDeviceLabel(row)}`}
                            </Typography>
                          ) : null}
                          {row.representative_evidence_count != null ? (
                            <Typography variant="body2" color="text.secondary">
                              {`Representative evidence: ${row.representative_evidence_count}`}
                            </Typography>
                          ) : null}
                          <Typography variant="body2" color="text.secondary">
                            {`Safety Case: Not created yet`}
                          </Typography>
                        </Stack>

                        <Stack spacing={0.75} sx={{ borderLeft: { lg: '1px solid rgba(15, 23, 42, 0.08)' }, pl: { lg: 2 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Detected</Typography>
                          <Typography variant="body2">{formatEventTimestamp(row.first_detected_at || row.updated_at)}</Typography>
                          {resolveLatestObservationAt(row) ? (
                            <Typography variant="body2" color="text.secondary">
                              {`Latest observation: ${formatEventTimestamp(resolveLatestObservationAt(row))}`}
                            </Typography>
                          ) : null}
                          {row.observation_count != null ? (
                            <Typography variant="body2" color="text.secondary">
                              {`Observation count: ${row.observation_count}`}
                            </Typography>
                          ) : null}
                          <Typography variant="caption" color="text.secondary">{statusFreshnessLabel(row)}</Typography>
                        </Stack>

                        <Stack spacing={0.75} sx={{ borderLeft: { lg: '1px solid rgba(15, 23, 42, 0.08)' }, pl: { lg: 2 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Status</Typography>
                          <Chip size="small" label="Assessment required" color="info" variant="outlined" sx={{ alignSelf: 'flex-start' }} />
                          <Typography variant="body2" color="text.secondary">No Safety Case created yet</Typography>
                        </Stack>

                        <Stack spacing={0.75} sx={{ borderLeft: { lg: '1px solid rgba(15, 23, 42, 0.08)' }, pl: { lg: 2 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Next step</Typography>
                          <Typography variant="body2">
                            Review evidence and decide whether this recurring pattern should become a Safety Case.
                          </Typography>
                        </Stack>

                        <Stack spacing={1} justifyContent="center" sx={{ minWidth: { lg: 180 } }}>
                          <Button size="small" variant="contained" disabled={busy} onClick={primaryAction.onClick}>
                            {primaryAction.label}
                          </Button>
                          {canManageSafetyCases && normalizeText(row.review_status) === 'reviewed' ? (
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => void onSaveFinding(row)}>
                              Create Safety Case
                            </Button>
                          ) : null}
                        </Stack>
                      </Box>
                      <PortfolioTechnicalDetails
                        row={row}
                        testId={`portfolio-candidate-technical-${workflowKey}`}
                      />
                      </Stack>
                    </CardContent>
                  </Card>
                );
              }

              if (kind === 'case') {
                const safetyCase = entry as GroupedSafetyCase;
                const row = safetyCase.primaryRow;
                const busy = actionWorkflowId === String(row.workflow_id || '').trim();
                const latestScan = safetyCase.latestScan;
                const latestSignedScan = safetyCase.latestSignedScan;
                const latestScanScope = latestScan
                  ? (formatShortDate(latestScan.scopeStart) && formatShortDate(latestScan.scopeEnd)
                    ? `${formatShortDate(latestScan.scopeStart)} to ${formatShortDate(latestScan.scopeEnd)}`
                    : 'Not recorded')
                  : 'Not recorded';
                return (
                  <Card
                    key={safetyCase.key}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      ...(safetyCase.hasNewActivity ? {
                        borderColor: 'rgba(245, 158, 11, 0.35)',
                        bgcolor: 'rgba(255, 251, 235, 0.45)',
                      } : undefined),
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: 'grid',
                            gap: 2,
                            gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 0.9fr)' },
                            alignItems: 'start',
                          }}
                        >
                          <Stack spacing={0.75}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {`Safety Case: ${safetyCase.title}`}
                              </Typography>
                              {safetyCase.hasNewActivity ? (
                                <Chip size="small" label="New activity" color="warning" variant="outlined" />
                              ) : null}
                            </Stack>
                            <Typography variant="body2">
                              {`Originating recurring pattern: ${safetyCase.originatingRiskPattern}`}
                            </Typography>
                            {safetyCase.cameraLabel ? (
                              <Typography variant="body2">
                                {`Camera: ${safetyCase.cameraLabel}`}
                              </Typography>
                            ) : null}
                            {safetyCase.zoneLabel ? (
                              <Typography variant="body2">
                                {`Zone: ${safetyCase.zoneLabel}`}
                              </Typography>
                            ) : null}
                            {safetyCase.deviceLabel ? (
                              <Typography variant="body2">
                                {`Device: ${safetyCase.deviceLabel}`}
                              </Typography>
                            ) : null}
                            <Typography variant="body2">
                              {`Hazard Category: ${safetyCase.hazardCategory}`}
                            </Typography>
                            <Typography variant="body2">
                              {`Case state: ${safetyCase.caseState}`}
                            </Typography>
                            {safetyCase.reviewStateLabel ? (
                              <Typography variant="body2">
                                {`Review state: ${safetyCase.reviewStateLabel}`}
                              </Typography>
                            ) : null}
                            {safetyCase.outcomeStateLabel ? (
                              <Typography variant="body2">
                                {`Outcome state: ${safetyCase.outcomeStateLabel}`}
                              </Typography>
                            ) : null}
                            {safetyCase.owner ? (
                              <Typography variant="body2">{`Action owner: ${safetyCase.owner}`}</Typography>
                            ) : (
                              <Typography variant="body2">Action owner: Not assigned</Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">{statusFreshnessLabel(row)}</Typography>
                          </Stack>

                          <Stack spacing={1.25}>
                            <Box
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                bgcolor: 'grey.50',
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                Latest Safety Scan
                              </Typography>
                              <Stack spacing={0.5}>
                                <Typography variant="body2">{`Status: ${latestScan?.statusLabel || 'Not started'}`}</Typography>
                                {latestScan?.statusKind === 'signed' && latestScan.signedAt ? (
                                  <Typography variant="body2">{`Signed: ${formatEventTimestamp(latestScan.signedAt)}`}</Typography>
                                ) : null}
                                <Typography variant="body2">{`Scope: ${latestScanScope}`}</Typography>
                                <Typography variant="body2">{`Package: ${latestScan?.packageAvailable ? 'Available' : 'Not available'}`}</Typography>
                              </Stack>
                            </Box>

                            {latestSignedScan && safetyCase.hasNewActivity ? (
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: '1px solid rgba(245, 158, 11, 0.25)',
                                  bgcolor: 'rgba(255, 251, 235, 0.65)',
                                }}
                              >
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                  New activity since latest Signed Safety Scan
                                </Typography>
                                <Stack spacing={0.5}>
                                  <Typography variant="body2">{`Latest observation: ${formatEventTimestamp(safetyCase.latestObservationAt)}`}</Typography>
                                  {(safetyCase.cameraLabel || safetyCase.zoneLabel) ? (
                                    <Typography variant="body2">
                                      {`Source: ${[safetyCase.cameraLabel ? `Camera ${safetyCase.cameraLabel}` : null, safetyCase.zoneLabel].filter(Boolean).join(' / ')}`}
                                    </Typography>
                                  ) : null}
                                  {safetyCase.newActivityCount != null ? (
                                    <Typography variant="body2">{formatNewActivityCountSummary({ count: safetyCase.newActivityCount, signedScanBoundary: latestSignedScan?.signedAt || null })}</Typography>
                                  ) : null}
                                  <Typography variant="body2">{`Review state: ${safetyCase.reviewState || 'Pending review'}`}</Typography>
                                </Stack>
                              </Box>
                            ) : null}
                          </Stack>

                          <Stack spacing={1} sx={{ minWidth: { xl: 220 } }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Actions</Typography>
                            {resolveSafetyCaseId(row) ? (
                              <Button size="small" variant="contained" disabled={busy} onClick={() => onOpenLiveCase(row)}>
                                Open Safety Case
                              </Button>
                            ) : null}
                            {safetyCase.hasNewActivity ? (
                              <Button size="small" variant="outlined" disabled={busy} onClick={() => onOpenReview(row)}>
                                Review New Activity
                              </Button>
                            ) : null}
                            {latestScan?.statusKind === 'signed' ? (
                              <Button size="small" variant="outlined" disabled={busy} onClick={() => void onViewSignedScan(latestScan.actionRow)}>
                                View Signed Safety Scan
                              </Button>
                            ) : null}
                            {canManageSafetyCases && latestScan?.statusKind !== 'signed' && resolveDraftSafetyScanReportId(latestScan?.actionRow || row) ? (
                              <Button size="small" variant="outlined" disabled={busy} onClick={() => void onOpenDraftScan(latestScan?.actionRow || row)}>
                                Open Draft Safety Scan
                              </Button>
                            ) : null}
                            {canManageSafetyCases ? (
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => void onCreateOrResumeDraft(row)}>
                              {latestSignedScan ? 'Create Updated Safety Scan' : 'Create Safety Scan'}
                            </Button>
                            ) : null}
                            {latestSignedScan?.packageAvailable && (canDownloadInternalPackage || canDownloadExternalSafePackage) ? (
                              <Button size="small" variant="outlined" disabled={busy} onClick={() => void onDownloadPackage(latestSignedScan.actionRow)}>
                                Download Package
                              </Button>
                            ) : null}
                            {latestSignedScan ? (
                              <FinalizedScanSecondaryExports
                                row={latestSignedScan.actionRow}
                                onDownloadCsv={onDownloadCsv}
                                onDownloadDocx={onDownloadDocx}
                                enabled={canDownloadCustomerPackage}
                              />
                            ) : null}
                          </Stack>
                        </Box>

                        <PortfolioTechnicalDetails
                          row={row}
                          testId={`portfolio-case-technical-${safetyCase.key}`}
                        />

                        <Divider />

                        <Box data-testid={`scan-archive-${safetyCase.key}`}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {ACCEPTANCE_UI_LABELS.viewScanArchive}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Past Safety Scans for this case.
                          </Typography>
                          {safetyCase.scanHistory.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No Safety Scans recorded yet.</Typography>
                          ) : (
                            <TableContainer>
                              <Table size="small" aria-label={`Scan archive for ${safetyCase.title}`}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Scan Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Revision</TableCell>
                                    <TableCell>Scope</TableCell>
                                    <TableCell>Signed Date</TableCell>
                                    <TableCell>Package</TableCell>
                                    <TableCell>New Activity After Scan</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {safetyCase.scanHistory.map((item) => {
                                    const itemScopeStart = formatShortDate(item.scopeStart);
                                    const itemScopeEnd = formatShortDate(item.scopeEnd);
                                    const itemScope = itemScopeStart && itemScopeEnd ? `${itemScopeStart} to ${itemScopeEnd}` : 'Not recorded';
                                    const revisionLabel = item.currentRevision != null
                                      ? `rev_${String(item.currentRevision).padStart(3, '0')}`
                                      : (item.statusKind === 'signed' ? 'Latest' : '—');
                                    const priorRevisions = item.revisions.filter(
                                      (revision) => item.currentRevision == null || revision.revision !== item.currentRevision,
                                    );
                                    return (
                                      <TableRow key={item.id} hover>
                                        <TableCell>{item.scanName}</TableCell>
                                        <TableCell>
                                          {collapseCompoundCustomerLabel(
                                            item.signatureState
                                              ? `${item.statusLabel} · ${item.signatureState}`
                                              : item.statusLabel,
                                          )}
                                        </TableCell>
                                        <TableCell>{revisionLabel}</TableCell>
                                        <TableCell>{itemScope}</TableCell>
                                        <TableCell>{item.statusKind === 'signed' && item.signedAt ? formatEventTimestamp(item.signedAt) : 'Not signed'}</TableCell>
                                        <TableCell>{item.packageAvailable ? 'Available' : 'Not available'}</TableCell>
                                        <TableCell>{item.hasNewActivityAfterScan ? 'Yes' : 'No'}</TableCell>
                                        <TableCell align="right">
                                          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                            {item.statusKind === 'signed' ? (
                                              <>
                                                <Button size="small" variant="text" onClick={() => void onViewSignedScan(item.actionRow)}>
                                                  View
                                                </Button>
                                                {item.packageAvailable && (canDownloadInternalPackage || canDownloadExternalSafePackage) ? (
                                                  <Button size="small" variant="text" onClick={() => void onDownloadPackage(item.actionRow)}>
                                                    Download
                                                  </Button>
                                                ) : null}
                                                {priorRevisions.length > 0 && canDownloadInternalPackage ? (
                                                  priorRevisions.slice(0, 3).map((revision) => (
                                                    <Button
                                                      key={`${item.id}-rev-${revision.revision}`}
                                                      size="small"
                                                      variant="text"
                                                      onClick={() => void onDownloadPackage(item.actionRow, { rev: revision.revision })}
                                                    >
                                                      {`rev_${String(revision.revision).padStart(3, '0')}`}
                                                    </Button>
                                                  ))
                                                ) : null}
                                              </>
                                            ) : (
                                              <Button size="small" variant="text" onClick={() => void onOpenDraftScan(item.actionRow)}>
                                                Open Draft
                                              </Button>
                                            )}
                                          </Stack>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              }

              const row = entry as PortfolioRow;
              const workflowKey = String(row.workflow_id || row.report_id || row.representative_incident_id || row.risk_type || title);
              const primaryAction = resolvePrimaryAction(row);
              return (
                <Card key={workflowKey} variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
                    <Box
                      sx={{
                        display: 'grid',
                        gap: 2,
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.35fr) auto' },
                        alignItems: 'start',
                      }}
                    >
                      <Stack spacing={1.25}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {hazardCategoryCardTitle(row)}
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {`${Number(row.workflow_count || 0)} Safety Case${Number(row.workflow_count || 0) === 1 ? '' : 's'}`}
                          </Typography>
                          <Typography variant="body2">{`Latest scan state: ${categoryLatestScanStateLabel(row)}`}</Typography>
                          <Typography variant="body2">
                            {`${Number(row.finalized_count || 0)} Signed Safety Scan${Number(row.finalized_count || 0) === 1 ? '' : 's'}`}
                          </Typography>
                          <Typography variant="body2">
                            {`${Number(row.blocked_count || 0)} Safety Scan${Number(row.blocked_count || 0) === 1 ? '' : 's'} blocked`}
                          </Typography>
                        </Stack>

                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>What this shows</Typography>
                          <Typography variant="body2">
                            A site-level view of repeated safety risk in this hazard category.
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack spacing={1} justifyContent="flex-start" sx={{ minWidth: { lg: 220 } }}>
                        <Button size="small" variant="contained" onClick={primaryAction.onClick}>
                          {primaryAction.label}
                        </Button>
                        {row.package_available ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => void onOpenDetail(row, { surface: 'package', proof_focus: 'report_package' })}
                          >
                            Download Summary
                          </Button>
                        ) : null}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const SafetyReportsDashboardPortfolio: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { effectiveUser: user } = useAuth();
  const capabilities = getCapabilitiesFromUser(user);
  const canManageSafetyCases = canManageSafetyCaseArtifacts(user);
  const canAssignOwner = canAssignSafetyCaseOwner(user);
  const canAccessScheduledScans = hasAnyCapability(
    capabilities,
    ['scan:configure', 'scan:run'],
  );
  const canManagePortfolioAccess = canManageAccessScope(capabilities);
  const canDownloadInternal = canDownloadInternalPackage(user);
  const canDownloadCustomer = canDownloadCustomerPackage(user);
  const canDownloadExternalSafe = canDownloadExternalSafePackage(user);
  const storedScope = useMemo(() => ensureUiScope(), []);
  const scope = useMemo(() => resolveWorkspacePortfolioScope({
    storedOrgId: storedScope.orgId,
    storedSiteId: storedScope.siteId,
    searchParams,
  }), [searchParams, storedScope.orgId, storedScope.siteId]);
  const [dashboard, setDashboard] = useState<{ summary?: SafetyDashboardSummary; workflows?: SafetyDashboardRow[] } | null>(null);
  const [portfolio, setPortfolio] = useState<SafetyPortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionWorkflowId, setActionWorkflowId] = useState<string | null>(null);
  const [riskPatternDrawerRow, setRiskPatternDrawerRow] = useState<PortfolioRow | null>(null);
  const [newActivityDrawerRow, setNewActivityDrawerRow] = useState<PortfolioRow | null>(null);
  const [timeWindowMenuAnchorEl, setTimeWindowMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [dashboardFetchLimit, setDashboardFetchLimit] = useState(DASHBOARD_FETCH_PAGE);
  const [scopeSites, setScopeSites] = useState<Site[]>([]);
  const [scopeSitesLoading, setScopeSitesLoading] = useState(false);
  const [scopeSitesError, setScopeSitesError] = useState<string | null>(null);
  const reportFrom = searchParams.get('report_from') || undefined;
  const reportTo = searchParams.get('report_to') || undefined;
  const activeTab = resolveWorkspaceTab(searchParams.get('tab'));
  const attentionFilter = resolveAttentionFilter(searchParams.get(attentionFilterParam()));
  const activeSavedView = resolveSavedView(searchParams.get(savedViewParam()));
  const actorIdentity = String(user?.username || user?.email || '').trim();
  const loadRequestRef = useRef(0);

  const loadDashboard = useCallback(async (opts?: { limit?: number; append?: boolean }) => {
    const requestId = ++loadRequestRef.current;
    const requestLimit = opts?.limit ?? DASHBOARD_FETCH_PAGE;
    if (opts?.append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      if (scope.mode === 'org') {
        const response = await reportsApi.getSafetyPortfolio({
          org_id: scope.orgId,
          limit: requestLimit,
          report_from: reportFrom,
          report_to: reportTo,
        });
        if (requestId !== loadRequestRef.current) return;
        setPortfolio(response);
        setDashboard({
          summary: response.summary?.operational_counts || {},
          workflows: [],
        });
      } else {
        const response = await reportsApi.getSafetyDashboard({
          org_id: scope.orgId,
          site_id: scope.siteId,
          limit: requestLimit,
          report_from: reportFrom,
          report_to: reportTo,
        });
        if (requestId !== loadRequestRef.current) return;
        setPortfolio(null);
        setDashboard({
          summary: response.summary || {},
          workflows: response.workflows || [],
        });
      }
      setDashboardFetchLimit(requestLimit);
    } catch (err: any) {
      if (requestId !== loadRequestRef.current) return;
      setError(err?.message || 'Safety dashboard unavailable');
      setPortfolio(null);
      if (!opts?.append) setDashboard({ summary: {}, workflows: [] });
    } finally {
      if (requestId === loadRequestRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [reportFrom, reportTo, scope.mode, scope.orgId, scope.siteId]);

  useEffect(() => {
    setDashboardFetchLimit(DASHBOARD_FETCH_PAGE);
    void loadDashboard({ limit: DASHBOARD_FETCH_PAGE });
  }, [loadDashboard]);

  useEffect(() => {
    if (!scope.orgId) {
      setScopeSites([]);
      setScopeSitesError(null);
      return;
    }
    let cancelled = false;
    setScopeSitesLoading(true);
    setScopeSitesError(null);
    sitesApi.listSites(scope.orgId)
      .then((sites) => {
        if (!cancelled) setScopeSites(sites);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setScopeSites([]);
          setScopeSitesError(err?.message || 'Unable to load site list');
        }
      })
      .finally(() => {
        if (!cancelled) setScopeSitesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scope.orgId]);

  const locationLookups = useSiteLocationLookups(
    scope.siteId,
    Boolean(scope.siteId) && scope.mode !== 'org',
  );

  const rows = useMemo(() => dashboard?.workflows ?? [], [dashboard?.workflows]);
  const windowLabel = formatWindowLabel(searchParams.get('report_from'), searchParams.get('report_to'));
  const updateSearchParams = useCallback((mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const handleSiteScopeChange = useCallback((nextSiteId: string) => {
    const trimmed = String(nextSiteId || '').trim();
    if (!trimmed) return;
    updateSearchParams((next) => {
      if (trimmed === WORKSPACE_ALL_AUTHORIZED_SITES) {
        next.set('scope', 'org');
        next.delete('site_id');
        if (scope.orgId) next.set('org_id', scope.orgId);
        return;
      }
      next.delete('scope');
      next.set('site_id', trimmed);
      if (scope.orgId) next.set('org_id', scope.orgId);
    });
  }, [scope.orgId, updateSearchParams]);
  const handleWorkspaceTabChange = useCallback((_: React.SyntheticEvent, nextTab: WorkspaceTab) => {
    updateSearchParams((next) => {
      next.set('tab', nextTab);
      next.delete(savedViewParam());
      if (nextTab !== 'attention') next.delete(attentionFilterParam());
    });
  }, [updateSearchParams]);
  const handleAttentionFilterToggle = useCallback((key: AttentionMetricKey) => {
    updateSearchParams((next) => {
      next.set('tab', 'attention');
      next.delete(savedViewParam());
      if (resolveAttentionFilter(next.get(attentionFilterParam())) === key) {
        next.delete(attentionFilterParam());
      } else {
        next.set(attentionFilterParam(), key);
      }
    });
  }, [updateSearchParams]);
  const handleRegisterLoadMoreFetch = useCallback(() => {
    const nextLimit = nextDashboardFetchLimit(dashboardFetchLimit);
    if (nextLimit <= dashboardFetchLimit) return;
    void loadDashboard({ limit: nextLimit, append: true });
  }, [dashboardFetchLimit, loadDashboard]);
  const handleTimeWindowSelect = useCallback((optionKey: TimeWindowOptionKey) => {
    if (optionKey === 'custom_range') return;
    updateSearchParams((next) => {
      if (optionKey === 'all_time') {
        next.delete('report_from');
        next.delete('report_to');
        return;
      }
      const range = optionKey === 'today'
        ? buildUtcDayRange(1)
        : optionKey === 'last_7_days'
          ? buildUtcDayRange(7)
          : buildUtcDayRange(30);
      next.set('report_from', range.from);
      next.set('report_to', range.to);
    });
    setTimeWindowMenuAnchorEl(null);
  }, [updateSearchParams]);
  const buildBuyerScanPathForRow = useCallback((row: PortfolioRow, reportId?: string | null) => {
    const id = String(reportId || '').trim();
    if (!id) return '';
    return safetyScanVerifyPath(id, {
      orgId: scope.orgId,
      siteId: String(row.site_id || scope.siteId || '').trim() || null,
      safetyCaseId: resolveSafetyCaseId(row) || null,
      incidentId: row.representative_incident_id || null,
    });
  }, [scope.orgId, scope.siteId]);
  const buildSafetyScanHandoffPathForRow = useCallback((row: PortfolioRow, reportId?: string | null) => {
    if (row.report_type === 'category') return null;
    if (normalizeText(row.report_status) === 'finalized') return null;
    if (!resolveSafetyCaseId(row) && !row.finding_saved) return null;
    const draftId = String(reportId || row.active_draft_report_id || '').trim();
    return draftId ? buildBuyerScanPathForRow(row, draftId) : null;
  }, [buildBuyerScanPathForRow]);

  const buildDetailFallbackPath = useCallback((row: PortfolioRow) => {
    if (row.report_type === 'category') {
      return buildHazardClassReportPath(String(row.risk_type || '').trim(), {
        orgId: scope.orgId,
        siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
        reportFrom: searchParams.get('report_from'),
        reportTo: searchParams.get('report_to'),
        deviceId: searchParams.get('device_id'),
        zoneId: searchParams.get('zone_id'),
      });
    }
    const finalizedRow = normalizeText(row.report_status) === 'finalized';
    const finalizedReportId = String(row.latest_finalized_report_id || row.report_id || '').trim();
    if (finalizedRow && finalizedReportId) {
      return buildSafetyReportArtifactPath({
        reportId: finalizedReportId,
        orgId: scope.orgId,
        siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
        deviceId: row.device_id || undefined,
        zoneId: row.zone_id || undefined,
        cameraId: row.camera_id || undefined,
        policyType: row.risk_type || undefined,
        policyName: row.risk_label || undefined,
      });
    }
    const handoffPath = buildSafetyScanHandoffPathForRow(row);
    if (handoffPath) return handoffPath;
    return buildArtifactFlowPath({
      kind: 'proof_pack',
      task: 'investigate_incident',
      orgId: scope.orgId,
      siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
      deviceId: row.device_id || undefined,
      zoneId: row.zone_id || undefined,
      cameraId: row.camera_id || undefined,
      reportFrom: searchParams.get('report_from'),
      reportTo: searchParams.get('report_to'),
      caseId: finalizedRow ? undefined : row.representative_incident_id || undefined,
      incidentId: finalizedRow ? undefined : row.representative_incident_id || undefined,
      policyType: row.risk_type || undefined,
      policyName: row.risk_label || undefined,
    });
  }, [buildSafetyScanHandoffPathForRow, scope.orgId, scope.siteId, searchParams]);

  const openReportDetail = useCallback(async (row: PortfolioRow, extraParams: Record<string, string> = {}) => {
    const fallbackPath = buildDetailFallbackPath(row);
    if (row.report_type === 'category') {
      navigate(appendQuery(fallbackPath, extraParams));
      return;
    }
    const finalizedRow = normalizeText(row.report_status) === 'finalized';
    const reportId = String(
      (finalizedRow
        ? row.latest_finalized_report_id || row.report_id || row.active_draft_report_id
        : row.active_draft_report_id || row.latest_finalized_report_id || row.report_id)
      || row.report_id
      || '',
    ).trim();
    const nextPath = await resolveSafetyReportOpenPath({
      fallbackPath,
      persistedReportId: reportId || undefined,
    });
    navigate(appendQuery(nextPath, extraParams));
  }, [buildDetailFallbackPath, navigate]);

  const downloadPackage = useCallback(async (
    row: PortfolioRow,
    options?: { rev?: number | null; forcePdf?: boolean },
  ) => {
    const rowStatus = normalizeText(row.report_status);
    if (rowStatus !== 'finalized') {
      await openReportDetail(row, { surface: 'package', proof_focus: 'report_package' });
      return;
    }
    const workflowId = String(row.workflow_id || row.report_id || '').trim();
    const reportId = String(row.latest_finalized_report_id || row.report_id || '').trim();
    if (!reportId) {
      await openReportDetail(row);
      return;
    }
    setActionWorkflowId(workflowId || reportId);
    setError(null);
    try {
      const rev = options?.rev != null && Number(options.rev) >= 1 ? Number(options.rev) : null;
      if (rev != null && options?.forcePdf) {
        const { blob, filename } = await reportsApi.downloadSafetyReportArtifactPdf(reportId, { rev });
        downloadBlob(blob, filename);
        return;
      }
      const { blob, filename } = canDownloadInternal
        ? await reportsApi.downloadSafetyReportArtifact(reportId)
        : await reportsApi.downloadExternalSafetyScanPackage(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setError(err?.message || 'Unable to download the Signed Safety Scan package.');
    } finally {
      setActionWorkflowId(null);
    }
  }, [canDownloadInternal, openReportDetail]);

  const downloadCsv = useCallback(async (row: PortfolioRow) => {
    if (normalizeText(row.report_status) !== 'finalized') return;
    const workflowId = String(row.workflow_id || row.report_id || '').trim();
    const reportId = String(row.latest_finalized_report_id || row.report_id || '').trim();
    if (!reportId) {
      await openReportDetail(row);
      return;
    }
    setActionWorkflowId(workflowId || reportId);
    setError(null);
    try {
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactCsv(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setError(err?.message || 'Unable to download the signed Safety Scan CSV.');
    } finally {
      setActionWorkflowId(null);
    }
  }, [openReportDetail]);

  const downloadDocx = useCallback(async (row: PortfolioRow) => {
    if (normalizeText(row.report_status) !== 'finalized') return;
    const workflowId = String(row.workflow_id || row.report_id || '').trim();
    const reportId = String(row.latest_finalized_report_id || row.report_id || '').trim();
    if (!reportId) {
      await openReportDetail(row);
      return;
    }
    setActionWorkflowId(workflowId || reportId);
    setError(null);
    try {
      const { blob, filename } = await reportsApi.downloadSafetyReportArtifactDocx(reportId);
      downloadBlob(blob, filename);
    } catch (err: any) {
      setError(err?.message || 'Unable to download the signed Safety Scan DOCX.');
    } finally {
      setActionWorkflowId(null);
    }
  }, [openReportDetail]);

  const openReview = useCallback((row: PortfolioRow) => {
    if (
      Boolean(resolveSignedSafetyScanReportId({
        latest_finalized_report_id: row.latest_finalized_report_id || row.report_id,
        report_id: row.latest_finalized_report_id || row.report_id,
        report_status: row.latest_finalized_report_id ? 'finalized' : row.report_status,
      }))
      && hasNewActivitySinceSignedScan(row)
    ) {
      navigate(buildAfterSignedScanReviewPath(row, scope.siteId, {
        fromDate: searchParams.get('report_from') || searchParams.get('from_date') || undefined,
        toDate: searchParams.get('report_to') || searchParams.get('to_date') || undefined,
      }));
      return;
    }
    navigate(buildReviewPath(row, scope.siteId));
  }, [navigate, scope.siteId, searchParams]);

  const openLiveCase = useCallback((row: PortfolioRow) => {
    const caseId = resolveSafetyCaseId(row);
    if (!caseId) return;
    navigate(buildLiveSafetyCasePath({
      caseId,
      orgId: scope.orgId,
      siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
      workflowId: row.workflow_id || undefined,
      incidentId: row.representative_incident_id || undefined,
    }));
  }, [navigate, scope.orgId, scope.siteId]);

  const openDraftScan = useCallback((row: PortfolioRow) => {
    const draftReportId = resolveDraftSafetyScanReportId(row);
    if (!draftReportId) return;
    const nextPath = buildBuyerScanPathForRow(row, draftReportId);
    if (!nextPath) return;
    navigate(nextPath);
  }, [buildBuyerScanPathForRow, navigate]);

  const openSafetyIntelligence = useCallback((row?: PortfolioRow | null) => {
    const next = new URLSearchParams();
    next.set('site_id', String(row?.site_id || scope.siteId || '').trim() || String(scope.siteId || ''));
    if (searchParams.get('report_from')) next.set('report_from', String(searchParams.get('report_from')));
    if (searchParams.get('report_to')) next.set('report_to', String(searchParams.get('report_to')));
    navigate(`/acceptance/overview?${next.toString()}`);
  }, [navigate, scope.siteId, searchParams]);

  const saveFinding = useCallback(async (row: PortfolioRow) => {
    const workflowId = String(row.workflow_id || '').trim();
    if (!workflowId) {
      openReview(row);
      return;
    }
    setActionWorkflowId(workflowId);
    setError(null);
    try {
      await reportsApi.saveSafetyFinding(workflowId, {
        representative_incident_id: row.representative_incident_id || undefined,
        scope: {
          site_id: row.site_id || scope.siteId || undefined,
          zones: row.zone_id ? [String(row.zone_id)] : [],
          risk_type: row.risk_type || undefined,
        },
        time_window: {
          start: searchParams.get('report_from') || undefined,
          end: searchParams.get('report_to') || undefined,
        },
        source_pattern_id: workflowId,
        source_group_key: workflowId,
        hazard_category: row.risk_type || undefined,
        finding_type: row.risk_type || undefined,
      });
      await loadDashboard();
    } catch (err: any) {
      setError(err?.message || 'Unable to create the Safety Case.');
    } finally {
      setActionWorkflowId(null);
    }
  }, [loadDashboard, openReview, scope.siteId, searchParams]);

  const createOrResumeDraft = useCallback(async (row: PortfolioRow) => {
    const workflowId = String(row.workflow_id || '').trim();
    if (!workflowId) {
      openReview(row);
      return;
    }
    const isUpdatedScan = Boolean(row.has_new_activity_since_report || row.workflow_changed_since_signoff);
    const signedAt = String(row.report_finalized_at || row.latest_scan_signed_at || '').trim() || undefined;
    const handoff = buildHandoffFromDashboardRow(
      row,
      { orgId: scope.orgId, siteId: scope.siteId },
      isUpdatedScan
        ? {
            scanScopeStart: signedAt,
            scanScopeEnd: searchParams.get('report_to') || new Date().toISOString(),
            isUpdatedScan: true,
          }
        : {
            scanScopeStart: searchParams.get('report_from') || undefined,
            scanScopeEnd: searchParams.get('report_to') || undefined,
            isUpdatedScan: false,
          },
    );
    if (row.active_draft_report_id) {
      const nextPath = buildBuyerScanPathForRow(row, row.active_draft_report_id);
      if (nextPath) navigate(nextPath);
      return;
    }

    setActionWorkflowId(workflowId);
    setError(null);
    try {
      const response = await reportsApi.createOrResumeSafetyReportDraft(
        workflowId,
        buildCreateDraftPayloadFromHandoff(handoff),
      );
      await loadDashboard();
      const draftReportId = String(response.report?.report_id || '').trim();
      const nextPath = buildBuyerScanPathForRow(row, draftReportId);
      if (nextPath) navigate(nextPath);
    } catch (err: any) {
      setError(err?.message || 'Unable to generate the Safety Scan package or resume the draft Safety Scan.');
    } finally {
      setActionWorkflowId(null);
    }
  }, [buildBuyerScanPathForRow, loadDashboard, navigate, openReview, scope.orgId, scope.siteId, searchParams]);

  const patternRows = useMemo<PortfolioRow[]>(
    () => rows
      .map((row) => ({ ...row, report_type: row.report_type || 'pattern' }))
      .sort((left, right) => {
        const leftNeedsAttention = isCandidateRiskPattern(left) ? 0 : 1;
        const rightNeedsAttention = isCandidateRiskPattern(right) ? 0 : 1;
        if (leftNeedsAttention !== rightNeedsAttention) return leftNeedsAttention - rightNeedsAttention;
        return String(right.updated_at || '').localeCompare(String(left.updated_at || ''));
      }),
    [rows],
  );
  const candidateRows = useMemo(
    () => dedupeCandidateRiskPatterns(patternRows.filter((row) => isCandidateRiskPattern(row))),
    [patternRows],
  );
  const safetyCaseGroups = useMemo(() => groupSafetyCases(patternRows, locationLookups), [locationLookups, patternRows]);
  const managedSafetyCaseGroups = useMemo(() => safetyCaseGroups.filter((safetyCase) => (
    isAdmittedSafetyCase(safetyCase.primaryRow)
    && !isEngineeringDemoArtifact(safetyCase.title)
    && !isEngineeringDemoArtifact(safetyCase.originatingRiskPattern)
  )), [safetyCaseGroups]);
  const categoryRows = useMemo<PortfolioRow[]>(() => {
    const grouped = new Map<string, GroupedSafetyCase[]>();
    managedSafetyCaseGroups.forEach((safetyCase) => {
      const row = safetyCase.primaryRow;
      const hazardClassKey = resolveIncidentHazardClassKey({
        policy_type: row.risk_type,
        incident_type: row.risk_type,
        zone_id: row.zone_id,
      });
      if (!hazardClassKey) return;
      const bucket = grouped.get(hazardClassKey) || [];
      bucket.push(safetyCase);
      grouped.set(hazardClassKey, bucket);
    });

    return V1_HAZARD_CLASS_OPTIONS.flatMap((option): PortfolioRow[] => {
      const groupedRows = grouped.get(option.key) || [];
      if (groupedRows.length === 0) return [];
      const blockedCount = groupedRows.filter((item) => item.scanHistory.some((scan) => scan.statusKind !== 'signed')).length;
      const finalizedCount = groupedRows.filter((item) => item.scanHistory.some((scan) => scan.statusKind === 'signed')).length;
      const impactPendingCount = groupedRows.filter((item) => String(item.primaryRow.impact_measurement_status || '').trim().toLowerCase() !== 'completed').length;
      const worseningCount = groupedRows.filter((item) => String(item.primaryRow.exposure_change_direction || '').trim().toLowerCase() === 'worse').length;
      const reviewedCount = groupedRows.filter((item) => String(item.primaryRow.review_status || '').trim().toLowerCase() === 'reviewed').length;
      const firstRow = groupedRows[0]?.primaryRow;
      const latestUpdatedAt = groupedRows
        .map((item) => String(item.primaryRow.updated_at || '').trim())
        .filter(Boolean)
        .sort()
        .reverse()[0] || null;
      const topArea = firstRow?.area_label || 'Multiple areas';
      return [{
        report_type: 'category',
        workflow_id: `hazard_class:${option.key}`,
        risk_type: option.key,
        risk_label: option.displayName,
        site_id: scope.siteId,
        site_label: scope.siteId,
        area_label: topArea,
        zone_id: firstRow?.zone_id || null,
        review_status: reviewedCount === groupedRows.length ? 'reviewed' : reviewedCount > 0 ? 'mixed' : 'pending',
        review_status_label: `${reviewedCount}/${groupedRows.length} reviewed`,
        corrective_action_status: blockedCount > 0 ? 'mixed' : 'covered',
        corrective_action_status_label: blockedCount > 0 ? `${blockedCount} blocked` : 'Portfolio covered',
        impact_measurement_status: impactPendingCount > 0 ? 'mixed' : 'covered',
        impact_status_label: impactPendingCount > 0 ? `${impactPendingCount} pending` : 'Coverage available',
        measurement_trust_level: 'category_summary',
        measurement_trust: {
          level: 'category_summary',
          effectiveness_claim_allowed: false,
          reason: option.claimBoundary,
          claim_boundary: option.claimBoundary,
        },
        effectiveness_claim_allowed: false,
        report_status: 'category_summary',
        finalization_status: 'on_demand',
        package_available: true,
        exposure_change_direction: worseningCount > 0
          ? 'worse'
          : groupedRows.some((item) => String(item.primaryRow.exposure_change_direction || '').trim().toLowerCase() === 'improved')
            ? 'improved'
            : undefined,
        recommended_next_step: worseningCount > 0
          ? 'review_worsening_workflows'
          : blockedCount > 0
            ? 'resolve_blocked_pattern_reports'
            : impactPendingCount > 0
              ? 'complete_impact_coverage'
              : 'open_category_report',
        blockers: [
          { code: 'workflow_count', message: `${groupedRows.length} Safety Case${groupedRows.length === 1 ? '' : 's'} roll up into this category summary.` },
          ...(blockedCount > 0 ? [{ code: 'blocked_reports', message: `${blockedCount} Safety Scan${blockedCount === 1 ? '' : 's'} blocked before sign-off.` }] : []),
          ...(impactPendingCount > 0 ? [{ code: 'impact_pending', message: `${impactPendingCount} Safety Case${impactPendingCount === 1 ? '' : 's'} still have recheck pending.` }] : []),
        ],
        workflow_count: groupedRows.length,
        blocked_count: blockedCount,
        finalized_count: finalizedCount,
        impact_pending_count: impactPendingCount,
        worsening_count: worseningCount,
        portfolio_summary: `${groupedRows.length} Safety Case${groupedRows.length === 1 ? '' : 's'} · ${finalizedCount} signed Safety Scan${finalizedCount === 1 ? '' : 's'} · ${worseningCount} worsening`,
        updated_at: latestUpdatedAt,
        actions: {
          open_report_detail: true,
          open_review: false,
          create_or_resume_draft: false,
          download_package: true,
        },
      } satisfies PortfolioRow];
    });
  }, [managedSafetyCaseGroups, scope.siteId]);

  const attentionBuilt = useMemo(() => buildWorkspaceAttention({
    candidates: candidateRows.map((row) => {
      const loc = resolvePortfolioPhysicalLocation(row, locationLookups, {
        zoneLabel: resolveZoneLabel(row, locationLookups),
        cameraName: resolveCameraLabel(row),
      });
      return {
      workflowId: row.workflow_id,
      title: buildRiskPatternTitle(row),
      riskLabel: row.risk_pattern_label || row.risk_label,
      areaLabel: row.area_label,
      zoneLabel: resolveZoneLabel(row, locationLookups),
      siteLabel: row.site_label || row.site_id,
      locationPath: loc.primary,
      locationSecondary: loc.secondary,
      owner: resolveCaseOwner(row),
      ownerIdentity: resolveCaseOwnerIdentity(row),
      updatedAt: row.updated_at,
      dueAt: resolveAttentionDueAt(row),
      observationCount: row.observation_count ?? row.grouped_incident_count,
    };
    }),
    safetyCases: managedSafetyCaseGroups.map((safetyCase) => {
      const primaryLoc = resolvePortfolioPhysicalLocation(safetyCase.primaryRow, locationLookups, {
        zoneLabel: safetyCase.zoneLabel,
        cameraName: resolveCameraLabel(safetyCase.primaryRow),
      });
      return {
      key: safetyCase.key,
      title: safetyCase.title,
      issueLabel: safetyCase.originatingRiskPattern,
      owner: safetyCase.owner,
      ownerIdentity: safetyCase.ownerIdentity,
      zoneLabel: safetyCase.zoneLabel,
      areaLabel: safetyCase.primaryRow.area_label,
      updatedAt: safetyCase.latestObservationAt || safetyCase.primaryRow.updated_at,
      dueAt: resolveAttentionDueAt(safetyCase.primaryRow),
      hasNewActivity: safetyCase.hasNewActivity,
      newActivityCount: safetyCase.newActivityCount,
      reviewState: safetyCase.reviewState || safetyCase.reviewStateLabel,
      currentReportId: safetyCase.currentScan?.reportId || safetyCase.primaryRow.active_draft_report_id || null,
      primary: {
        workflowId: safetyCase.primaryRow.workflow_id,
        reportId: safetyCase.currentScan?.reportId
          || safetyCase.primaryRow.active_draft_report_id
          || safetyCase.primaryRow.report_id,
        safetyCaseId: resolveSafetyCaseId(safetyCase.primaryRow),
        areaLabel: safetyCase.primaryRow.area_label,
        zoneLabel: safetyCase.zoneLabel,
        siteLabel: safetyCase.primaryRow.site_label || safetyCase.primaryRow.site_id,
        locationPath: primaryLoc.primary,
        locationSecondary: primaryLoc.secondary,
        owner: safetyCase.owner,
        ownerIdentity: safetyCase.ownerIdentity,
        updatedAt: safetyCase.primaryRow.updated_at,
        dueAt: resolveAttentionDueAt(safetyCase.primaryRow),
        impactMeasurementStatus: safetyCase.primaryRow.impact_measurement_status,
        recommendedNextStep: safetyCase.primaryRow.recommended_next_step,
        blockerCodes: (safetyCase.primaryRow.blockers || []).map((blocker) => String(blocker.code || '')),
        scanStatusKind: resolveScanStatus(safetyCase.primaryRow).kind,
        customerScanWorkflowState: safetyCase.primaryRow.customer_scan_workflow_state,
        customerAttentionWorkState: safetyCase.primaryRow.customer_attention_work_state,
      },
      awaitingScans: safetyCase.scanHistory
        .filter((scan) => scan.statusKind === 'draft' || scan.statusKind === 'awaiting_signoff')
        .map((scan) => {
          const scanLoc = resolvePortfolioPhysicalLocation(scan.actionRow, locationLookups, {
            zoneLabel: resolveZoneLabel(scan.actionRow, locationLookups),
            cameraName: resolveCameraLabel(scan.actionRow),
          });
          return {
          workflowId: scan.actionRow.workflow_id,
          reportId: scan.reportId,
          areaLabel: scan.actionRow.area_label,
          zoneLabel: resolveZoneLabel(scan.actionRow, locationLookups),
          siteLabel: scan.actionRow.site_label || scan.actionRow.site_id,
          locationPath: scanLoc.primary,
          locationSecondary: scanLoc.secondary,
          owner: resolveCaseOwner(scan.actionRow) || safetyCase.owner,
          ownerIdentity: resolveCaseOwnerIdentity(scan.actionRow) || safetyCase.ownerIdentity,
          updatedAt: scan.actionRow.updated_at,
          dueAt: resolveAttentionDueAt(scan.actionRow),
          impactMeasurementStatus: scan.actionRow.impact_measurement_status,
          recommendedNextStep: scan.actionRow.recommended_next_step,
          blockerCodes: (scan.actionRow.blockers || []).map((blocker) => String(blocker.code || '')),
          scanStatusKind: scan.statusKind,
          // Prefer Slice D customer_* on the action row. Inherit primary only when it is not
          // Signed (follow-up drafts must not inherit the parent's Signed badge).
          customerScanWorkflowState: (() => {
            if (scan.statusKind === 'signed') return 'Signed';
            const fromAction = scan.actionRow.customer_scan_workflow_state || null;
            if (fromAction) return fromAction;
            const fromPrimary = safetyCase.primaryRow.customer_scan_workflow_state || null;
            return fromPrimary && fromPrimary !== 'Signed' ? fromPrimary : null;
          })(),
          customerAttentionWorkState: (() => {
            const fromAction = scan.actionRow.customer_attention_work_state || null;
            if (fromAction && fromAction !== 'Signed') return fromAction;
            const fromPrimary = safetyCase.primaryRow.customer_attention_work_state || null;
            return fromPrimary && fromPrimary !== 'Signed' ? fromPrimary : null;
          })(),
        };
        }),
      };
    }),
    scanWorkItems: buildScanOnlyAttentionWorkItems({ safetyCaseGroups: safetyCaseGroups.filter((safetyCase) => !isAdmittedSafetyCase(safetyCase.primaryRow)), locationLookups, resolvePortfolioPhysicalLocation, resolveAttentionDueAt, resolveCaseOwner, resolveCaseOwnerIdentity, resolveScanStatus, resolveSafetyCaseId, resolveCameraLabel, resolveZoneLabel }),
  }), [candidateRows, locationLookups, managedSafetyCaseGroups, safetyCaseGroups]);
  const portfolioAttentionItems = useMemo(
    () => buildPortfolioAttentionItems(portfolio?.attention, portfolio?.site_coverage),
    [portfolio?.attention, portfolio?.site_coverage],
  );
  const portfolioAttentionMetrics = useMemo(
    () => buildPortfolioAttentionMetrics(portfolioAttentionItems),
    [portfolioAttentionItems],
  );
  const attentionItems = useMemo(
    () => {
      if (scope.mode === 'org') {
        const filtered = filterWorkspaceAttentionItems({
          phase1Items: portfolioAttentionItems.filter((item) => item.metricKey !== 'active_cases'),
          activeCaseItems: portfolioAttentionItems.filter((item) => item.metricKey === 'active_cases'),
        }, attentionFilter);
        if (activeSavedView !== 'my_open_work') return filtered;
        const actor = actorIdentity.toLowerCase();
        if (!actor) return [];
        return filtered.filter(
          (item) => String(item.ownerIdentity || '').trim().toLowerCase() === actor,
        );
      }
      const filtered = filterWorkspaceAttentionItems(attentionBuilt, attentionFilter);
      if (activeSavedView !== 'my_open_work') return filtered;
      const actor = actorIdentity.toLowerCase();
      if (!actor) return [];
      return filtered.filter(
        (item) => String(item.ownerIdentity || '').trim().toLowerCase() === actor,
      );
    },
    [activeSavedView, actorIdentity, attentionBuilt, attentionFilter, portfolioAttentionItems, scope.mode],
  );
  const handlePortfolioExport = useCallback(() => {
    if (!portfolio || scope.mode !== 'org') return;
    const exportedAt = new Date().toISOString();
    const csv = buildPortfolioExportCsv({
      portfolio,
      attentionItems,
      context: {
        exportedAt,
        timeWindowLabel: windowLabel,
        reportFrom: reportFrom || null,
        reportTo: reportTo || null,
        attentionFilter: attentionFilter || null,
        savedView: activeSavedView || null,
      },
    });
    const blob = createPortfolioExportBlob(csv);
    downloadBlob(blob, buildPortfolioExportFilename(portfolio.org_id, exportedAt));
  }, [
    activeSavedView,
    attentionFilter,
    attentionItems,
    portfolio,
    reportFrom,
    reportTo,
    scope.mode,
    windowLabel,
  ]);
  const riskPatternLinkSources = useMemo(
    () => managedSafetyCaseGroups.map((safetyCase) => ({
      id: String(resolveSafetyCaseId(safetyCase.primaryRow) || safetyCase.key || '').trim(),
      title: safetyCase.title,
      riskType: safetyCase.primaryRow.risk_type,
      zoneLabel: safetyCase.zoneLabel || safetyCase.primaryRow.area_label,
      siteId: safetyCase.primaryRow.site_id,
    })),
    [managedSafetyCaseGroups],
  );
  const closeRiskPatternDrawer = useCallback(() => setRiskPatternDrawerRow(null), []);
  const riskPatternDisposition = useRiskPatternDrawerDisposition({
    row: riskPatternDrawerRow,
    linkSources: riskPatternLinkSources,
    actorUser: user,
    siteId: scope.siteId,
    reportFrom,
    reportTo,
    onCloseDrawer: closeRiskPatternDrawer,
    onRefresh: loadDashboard,
    onGlobalError: setError,
  });
  const { resetDisposition, submit: submitDisposition, busy: dispositionBusy, error: dispositionError, linkableCases } = riskPatternDisposition;
  const newActivityUi = useNewActivityDrawerDecision({
    row: newActivityDrawerRow,
    setRow: setNewActivityDrawerRow,
    actorUser: user,
    canDecide: (
      hasCapability(capabilities, 'safety_case:update')
      && (
        !String(newActivityDrawerRow?.representative_incident_id || '').trim()
        || hasCapability(capabilities, 'incidents:write')
      )
    ),
    orgId: scope.orgId,
    siteId: scope.siteId,
    reportFrom,
    reportTo,
    liveCasePathForRow: (row) => {
      const caseId = resolveSafetyCaseId(row);
      return caseId ? buildLiveSafetyCasePath({
        caseId,
        orgId: scope.orgId,
        siteId: String(row.site_id || scope.siteId || '').trim() || undefined,
        workflowId: row.workflow_id || undefined,
        incidentId: row.representative_incident_id || undefined,
      }) : null;
    },
    onRefresh: loadDashboard,
    onNavigate: navigate,
    onReviewEvidenceFallback: openReview,
    onGlobalError: setError,
  });
  const openNewActivityDrawer = newActivityUi.open;
  // Legacy expanded-card helpers remain wired for download/create paths used outside Attention.
  void [
    PortfolioSection,
    canDownloadCustomer,
    canDownloadExternalSafe,
    actionWorkflowId,
    downloadPackage,
    downloadCsv,
    downloadDocx,
    openSafetyIntelligence,
    saveFinding,
    createOrResumeDraft,
    categoryRows,
  ];
  const handleAttentionAction = useCallback((item: WorkspaceAttentionItem) => {
    if (item.actionKind === 'open_site_workspace') {
      const siteId = String(item.siteId || '').trim();
      if (!siteId) return;
      updateSearchParams((next) => {
        next.delete('scope');
        next.set('site_id', siteId);
        if (scope.orgId) next.set('org_id', scope.orgId);
        next.set('tab', 'attention');
        next.delete(attentionFilterParam());
      });
      return;
    }
    if (item.actionKind === 'review_pattern') {
      const row = candidateRows.find((candidate) => String(candidate.workflow_id || '').trim() === item.sourceKey);
      if (row) openReview(row);
      return;
    }
    const safetyCase = safetyCaseGroups.find((entry) => (
      entry.key === item.sourceKey
      || entry.scanHistory.some((scan) => String(scan.reportId || scan.id).trim() === item.sourceKey)
    ));
    if (!safetyCase) return;
    if (item.actionKind === 'open_case' || item.actionKind === 'assign_owner') {
      openLiveCase(safetyCase.primaryRow);
      return;
    }
    if (item.actionKind === 'review_activity') {
      openNewActivityDrawer(safetyCase.primaryRow);
      return;
    }
    const scan = safetyCase.scanHistory.find((entry) => String(entry.reportId || entry.id).trim() === item.sourceKey);
    if (item.actionKind === 'resolve_coverage') {
      if (scan) {
        void openDraftScan(scan.actionRow || safetyCase.primaryRow);
        return;
      }
      openLiveCase(safetyCase.primaryRow);
      return;
    }
    void openDraftScan(scan?.actionRow || safetyCase.primaryRow);
  }, [candidateRows, openDraftScan, openLiveCase, openNewActivityDrawer, openReview, safetyCaseGroups, scope.orgId, updateSearchParams]);

  const handleRiskPatternDrawerClose = useCallback(() => {
    resetDisposition();
    setRiskPatternDrawerRow(null);
  }, [resetDisposition]);

  const handleRiskPatternReviewEvidence = useCallback(() => {
    const row = riskPatternDrawerRow;
    resetDisposition();
    setRiskPatternDrawerRow(null);
    if (row) openReview(row);
  }, [openReview, resetDisposition, riskPatternDrawerRow]);

  const managedSafetyCaseRows = useMemo(() => buildCaseRegisterRows(
    managedSafetyCaseGroups.map((safetyCase) => {
      const siteZone = resolvePortfolioRegisterLocationLabel(safetyCase.primaryRow, locationLookups, {
        zoneLabel: safetyCase.zoneLabel,
        cameraName: resolveCameraLabel(safetyCase.primaryRow),
      });
      const scanLinkArgs = { caseKey: safetyCase.key, caseId: resolveSafetyCaseId(safetyCase.primaryRow),
        definition: safetyCase.primaryRow.scan_template_label || safetyCase.primaryRow.scan_template || null, siteZone };
      const currentScanLink = buildRegisterCaseScanLinkSource({ ...scanLinkArgs, scan: safetyCase.currentScan }),
        latestSignedScanLink = buildRegisterCaseScanLinkSource({ ...scanLinkArgs, scan: safetyCase.latestSignedScan }),
        latestScanLink = buildRegisterCaseScanLinkSource({ ...scanLinkArgs, scan: safetyCase.latestScan });
      const currentWorkflowState = currentScanLink?.workflowState ?? null, latestWorkflowState = latestScanLink?.workflowState ?? null;
      return {
        key: safetyCase.key,
        caseId: resolveSafetyCaseId(safetyCase.primaryRow),
        title: safetyCase.title,
        condition: safetyCase.originatingRiskPattern,
        riskType: safetyCase.primaryRow.risk_type,
        hazardClassKey: safetyCase.primaryRow.hazard_category,
        siteArea: siteZone,
        cameraLabel: resolveCameraLabel(safetyCase.primaryRow),
        owner: safetyCase.owner,
        ownerIdentity: safetyCase.ownerIdentity,
        workflowStatus: safetyCase.primaryRow.workflow_status,
        workflowStatusLabel: safetyCase.primaryRow.workflow_status_label,
        correctiveActionStatus: safetyCase.primaryRow.corrective_action_status,
        correctiveActionStatusLabel: safetyCase.primaryRow.corrective_action_status_label,
        safetyCaseStatus: safetyCase.primaryRow.safety_case_status,
        safetyCaseStatusLabel: safetyCase.primaryRow.safety_case_status_label,
        customerControlStage: safetyCase.primaryRow.customer_control_stage,
        currentScanLabel: safetyCase.currentScan?.statusLabel ? (currentWorkflowState || safetyCase.currentScan.statusLabel) : null,
        currentScanWorkflowState: currentWorkflowState,
        currentScanResult: safetyCase.currentScan?.actionRow.customer_scan_result
          || safetyCase.primaryRow.customer_scan_result
          || null,
        currentOccurrenceCount: safetyCase.currentScan?.actionRow.observation_count
          ?? safetyCase.primaryRow.observation_count
          ?? null,
        currentScanLink,
        workflowId: safetyCase.primaryRow.workflow_id || safetyCase.key,
        latestScanLabel: safetyCase.latestScan?.statusLabel ? (latestWorkflowState || safetyCase.latestScan.statusLabel) : null,
        latestScanWorkflowState: latestWorkflowState,
        latestSignedScanLabel: safetyCase.latestSignedScan?.statusLabel
          ? `${buildSafetyScanName(safetyCase.latestSignedScan.scopeStart, safetyCase.latestSignedScan.scopeEnd)} · Signed`
          : null,
        latestSignedScanLink,
        scanCount: new Set(safetyCase.scanHistory.map((scan) => scan.logicalScanKey)).size,
        hasNewActivity: safetyCase.hasNewActivity,
        newActivityCount: safetyCase.newActivityCount,
        recommendedNextStep: safetyCase.primaryRow.recommended_next_step,
        dueAt: resolveAttentionDueAt(safetyCase.primaryRow),
        updatedAt: safetyCase.latestObservationAt || safetyCase.primaryRow.updated_at || null,
      };
    }),
  ), [locationLookups, managedSafetyCaseGroups]);

  const safetyScanRows = useMemo(() => buildScanRegisterRows(
    safetyCaseGroups.flatMap((safetyCase) => safetyCase.scanHistory.map((scan) => ({
      id: `${safetyCase.key}:${scan.id}`,
      reportId: scan.reportId,
      logicalScanKey: scan.logicalScanKey,
      caseKey: safetyCase.key,
      caseId: resolveSafetyCaseId(safetyCase.primaryRow),
      caseTitle: safetyCase.title,
      scanName: scan.scanName,
      definition: safetyCase.primaryRow.scan_template_label || safetyCase.primaryRow.scan_template || null,
      trigger: mapScanRegisterTrigger(scan.triggerType),
      siteZone: resolvePortfolioRegisterLocationLabel(safetyCase.primaryRow, locationLookups, {
        zoneLabel: safetyCase.zoneLabel,
        cameraName: resolveCameraLabel(safetyCase.primaryRow),
      }),
      windowStart: scan.scopeStart,
      windowEnd: scan.scopeEnd,
      reportStatus: scan.statusKind === 'signed'
        ? 'finalized'
        : scan.statusKind === 'awaiting_signoff'
          ? 'blocked'
          : (scan.actionRow.report_status || 'draft'),
      signoffStatus: scan.actionRow.signoff_status,
      historyStatus: scan.statusKind === 'signed' ? 'finalized' : scan.statusKind,
      impactMeasurementStatus: scan.actionRow.impact_measurement_status,
      recommendedNextStep: scan.actionRow.recommended_next_step,
      blockerCodes: (scan.actionRow.blockers || []).map((blocker) => String(blocker.code || '')),
      // Do not inherit parent-row customer_* onto synthetic history action rows.
      customerScanWorkflowState: scan.statusKind === 'signed' ? 'Signed' : scan.statusLabel,
      customerScanResult: scan.actionRow.customer_scan_result || null,
      measuredOccurrenceCount: scan.measuredOccurrenceCount ?? null,
      reviewedOccurrenceCount: scan.reviewedOccurrenceCount ?? null,
      confirmedOccurrenceCount: scan.confirmedOccurrenceCount ?? null,
      usableHours: scan.usableHours ?? null,
      coverageState: scan.coverageState ?? null,
      verificationState: scan.verificationState ?? null,
      completedAt: scan.signedAt || scan.scopeEnd || null,
    }))),
  ), [locationLookups, safetyCaseGroups]);

  const recordRows = useMemo(() => buildRecordRegisterRows(
    safetyCaseGroups.flatMap((safetyCase) => {
      const signedScans = safetyCase.scanHistory.filter((scan) => scan.statusKind === 'signed');
      const latestSignedId = safetyCase.latestSignedScan?.id || signedScans[0]?.id || null;
      return signedScans.map((scan) => ({
        id: `${safetyCase.key}:${scan.id}`,
        reportId: scan.reportId,
        caseKey: safetyCase.key,
        caseId: resolveSafetyCaseId(safetyCase.primaryRow),
        caseTitle: safetyCase.title,
        recordName: scan.scanName,
        revision: scan.currentRevision,
        revisions: scan.revisions,
        scopeStart: scan.scopeStart,
        scopeEnd: scan.scopeEnd,
        signedBy: scan.signedBy,
        signedAt: scan.signedAt,
        hasNewActivity: Boolean(scan.hasNewActivityAfterScan || (scan.id === latestSignedId && safetyCase.hasNewActivity)),
        packageAvailable: scan.packageAvailable,
        isLatestSigned: scan.id === latestSignedId,
      }));
    }),
  ), [safetyCaseGroups]);

  const canFetchMoreRegisters = canIncreaseDashboardFetch({
    fetchedCount: rows.length,
    fetchLimit: dashboardFetchLimit,
  }) && scope.mode !== 'org';

  const findSafetyCaseForRegister = useCallback((caseKey: string) => (
    safetyCaseGroups.find((entry) => entry.key === caseKey) || null
  ), [safetyCaseGroups]);

  const handleCaseRegisterAction = useWorkspaceCaseRegisterActions({
    findSafetyCaseForRegister,
    openNewActivityDrawer,
    openLiveCase,
    loadDashboard,
    setActionWorkflowId,
    setError,
  });

  const handleScanRegisterAction = useCallback((row: ScanRegisterRow) => {
    const reportId = String(row.reportId || '').trim();
    if (!reportId) return;
    const safetyCase = findSafetyCaseForRegister(row.caseKey);
    const scan = safetyCase?.scanHistory.find((entry) => `${safetyCase.key}:${entry.id}` === row.id);
    const actionRow = scan?.actionRow || safetyCase?.primaryRow;
    const nextPath = actionRow
      ? buildBuyerScanPathForRow(actionRow, reportId)
      : safetyScanVerifyPath(reportId, {
        orgId: scope.orgId,
        siteId: scope.siteId,
        safetyCaseId: row.caseId,
      });
    if (!nextPath) return;
    navigate(nextPath);
  }, [buildBuyerScanPathForRow, findSafetyCaseForRegister, navigate, scope.orgId, scope.siteId]);

  const handleCreateCaseFromScan = useCallback((row: ScanRegisterRow) => {
    const safetyCase = findSafetyCaseForRegister(row.caseKey);
    if (safetyCase?.primaryRow) {
      navigate(buildReviewPath(safetyCase.primaryRow, scope.siteId));
      return;
    }
    updateSearchParams((next) => {
      next.set('tab', 'attention');
    });
  }, [findSafetyCaseForRegister, navigate, scope.siteId, updateSearchParams]);

  const handleRecordRegisterAction = useCallback((row: RecordRegisterRow) => {
    const path = buildSignedRecordPath(row, searchParams);
    if (!path) {
      setError('The exact signed record revision is unavailable.');
      return;
    }
    navigate(path);
  }, [navigate, searchParams]);

  const handleRecordReviewActivity = useCallback((row: RecordRegisterRow) => {
    const safetyCase = findSafetyCaseForRegister(row.caseKey);
    if (safetyCase) openNewActivityDrawer(safetyCase.primaryRow);
  }, [findSafetyCaseForRegister, openNewActivityDrawer]);

  const handleRecordRegisterDownload = useCallback((row: RecordRegisterRow) => {
    if (row.revision == null) {
      setError('The exact signed record revision is unavailable.');
      return;
    }
    const safetyCase = findSafetyCaseForRegister(row.caseKey);
    if (!safetyCase) return;
    const scan = safetyCase.scanHistory.find((entry) => `${safetyCase.key}:${entry.id}` === row.id);
    if (scan) {
      void downloadPackage(scan.actionRow, { rev: row.revision, forcePdf: true });
      return;
    }
    void downloadPackage(safetyCase.primaryRow, { rev: row.revision, forcePdf: true });
  }, [downloadPackage, findSafetyCaseForRegister]);

  const nextNewActivityCase = useMemo(
    () => managedSafetyCaseRows.find((row) => caseRowHasNewActivity(row.newActivity)) || null,
    [managedSafetyCaseRows],
  );
  const primaryCta = useMemo(() => resolveWorkspacePrimaryCta({
    tab: activeTab,
    attentionItemCount: attentionItems.length,
    hasNewActivityCase: Boolean(nextNewActivityCase),
    canManageRecurringScans: Boolean(
      canAccessScheduledScans && scope.mode !== 'org' && scope.siteId,
    ),
  }), [
    activeTab,
    attentionItems.length,
    canAccessScheduledScans,
    nextNewActivityCase,
    scope.mode,
    scope.siteId,
  ]);

  const openManageRecurringScans = useCallback(() => {
    if (!scope.siteId) return;
    const next = new URLSearchParams();
    next.set('site_id', scope.siteId);
    if (reportFrom) next.set('report_from', reportFrom);
    if (reportTo) next.set('report_to', reportTo);
    navigate(`/acceptance/scan-definitions?${next.toString()}`);
  }, [navigate, reportFrom, reportTo, scope.siteId]);

  const handlePrimaryCta = useCallback(() => {
    if (primaryCta.kind === 'review_next_attention') {
      const nextItem = attentionItems[0];
      if (nextItem) handleAttentionAction(nextItem);
      return;
    }
    if (primaryCta.kind === 'open_next_new_activity_case' && nextNewActivityCase) {
      handleCaseRegisterAction(nextNewActivityCase);
      return;
    }
    if (primaryCta.kind === 'manage_recurring_scans') {
      openManageRecurringScans();
    }
  }, [
    attentionItems,
    handleAttentionAction,
    handleCaseRegisterAction,
    nextNewActivityCase,
    openManageRecurringScans,
    primaryCta.kind,
  ]);

  return (
    <Stack spacing={2} sx={{ width: '100%', maxWidth: 1440, pb: 2 }}>
      <WorkspaceHeaderChrome
        title={WORKSPACE_PAGE_TITLE}
        subtitle={WORKSPACE_PAGE_SUBTITLE}
        loading={loading}
        orgId={scope.orgId}
        siteId={scope.siteId}
        scopeMode={scope.mode}
        sites={scopeSites}
        sitesLoading={scopeSitesLoading}
        sitesError={scopeSitesError}
        onSiteChange={handleSiteScopeChange}
        windowLabel={windowLabel}
        timeWindowMenuAnchorEl={timeWindowMenuAnchorEl}
        onOpenTimeWindowMenu={(event) => setTimeWindowMenuAnchorEl(event.currentTarget)}
        onCloseTimeWindowMenu={() => setTimeWindowMenuAnchorEl(null)}
        timeWindowOptions={TIME_WINDOW_OPTIONS}
        onTimeWindowSelect={(key) => handleTimeWindowSelect(key as TimeWindowOptionKey)}
        primaryCta={primaryCta}
        onPrimaryCta={handlePrimaryCta}
        showPortfolioExport={Boolean(scope.mode === 'org' && portfolio)}
        onPortfolioExport={handlePortfolioExport}
      />

      <WorkspaceNavGroups value={activeTab} onChange={handleWorkspaceTabChange} />
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="workspace-tab-helper"
        sx={{ display: 'block', mt: -1, mb: 0.5 }}
      >
        {WORKSPACE_TAB_HELPERS[activeTab]}
      </Typography>

      <RiskPatternAttentionDrawer
        open={Boolean(riskPatternDrawerRow)}
        row={riskPatternDrawerRow}
        onClose={handleRiskPatternDrawerClose}
        onReviewEvidence={handleRiskPatternReviewEvidence}
        canDispose={canManageSafetyCases}
        dispositionBusy={dispositionBusy}
        dispositionError={dispositionError}
        linkableCases={linkableCases}
        actorUser={user}
        onDisposition={(kind, payload) => { void submitDisposition(kind, payload); }}
      />

      <NewActivityAttentionDrawer {...newActivityUi.drawerProps} />

      {scope.mode === 'org' && portfolio ? (
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Alert severity={portfolio.permissions?.partial || portfolio.scope?.partial ? 'warning' : 'info'} data-testid="workspace-portfolio-coverage">
              Portfolio coverage: {formatSiteCoverageSummary(portfolio.site_coverage)}. Counts require authorized site coverage, configured observation scope, coverage and measurement state, and review status.
            </Alert>
            {portfolio.permissions?.partial || portfolio.scope?.partial ? (
              <Alert severity="warning" data-testid="workspace-portfolio-partial">
                Portfolio is partial. Permission-denied or unavailable sites are not mixed into Attention counts.
              </Alert>
            ) : null}
          </Stack>
          <WorkspaceSiteComparisonPanel
            siteCoverage={portfolio.site_coverage}
            loading={loading}
            onOpenSite={handleSiteScopeChange}
          />
          <WorkspacePortfolioGovernancePanel
            portfolio={portfolio}
            loading={loading}
            canManageAccessScope={canManagePortfolioAccess}
          />
        </Stack>
      ) : null}

      {activeTab === 'attention' ? (
        <Stack spacing={3}>
          <WorkspaceAttentionPanel
            metrics={scope.mode === 'org' ? portfolioAttentionMetrics : attentionBuilt.metrics}
            items={attentionItems}
            activeFilter={attentionFilter}
            loading={loading}
            error={error}
            onToggleFilter={handleAttentionFilterToggle}
            onRetry={() => { void loadDashboard(); }}
            onAction={handleAttentionAction}
          />
        </Stack>
      ) : (
        <WorkspaceRegistersPanel
          kind={activeTab === 'cases' || activeTab === 'scans' || activeTab === 'records' ? activeTab : 'cases'}
          siteId={scope.siteId}
          portfolioMode={scope.mode === 'org'}
          canViewScheduledScans={canAccessScheduledScans}
          actorIdentity={actorIdentity}
          caseRows={managedSafetyCaseRows}
          scanRows={safetyScanRows}
          recordRows={recordRows}
          loading={loading}
          loadingMore={loadingMore}
          error={error}
          canFetchMore={canFetchMoreRegisters}
          onRetry={() => { void loadDashboard({ limit: dashboardFetchLimit }); }}
          onLoadMoreFetch={handleRegisterLoadMoreFetch}
          onCaseAction={handleCaseRegisterAction}
          canAssignOwner={canAssignOwner}
          onOwnerAssigned={() => { void loadDashboard(); }}
          onScanAction={handleScanRegisterAction}
          onCreateCase={handleCreateCaseFromScan}
          onRecordAction={handleRecordRegisterAction}
          onRecordReviewActivity={handleRecordReviewActivity}
          onRecordDownload={handleRecordRegisterDownload}
        />
      )}
    </Stack>
  );
};

export default SafetyReportsDashboardPortfolio;
